import { defaultKeymap, history, historyKeymap, indentWithTab, isolateHistory, redo, undo } from "@codemirror/commands";
import {
  HighlightStyle,
  bracketMatching,
  indentUnit,
  syntaxHighlighting,
  syntaxTree,
} from "@codemirror/language";
import { searchKeymap } from "@codemirror/search";
import {
  Compartment,
  EditorSelection,
  EditorState,
  Facet,
  countColumn,
  findColumn,
  MapMode,
  Prec,
  RangeSetBuilder,
  StateEffect,
  StateField,
  Transaction,
  type Range,
} from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  type ViewUpdate,
  ViewPlugin,
  WidgetType,
  drawSelection,
  dropCursor,
  highlightSpecialChars,
  keymap,
  lineNumbers,
} from "@codemirror/view";
import { markdownLanguage } from "@codemirror/lang-markdown";
import { tags } from "@lezer/highlight";
import { findChangedRange } from "./editor-sync";
import { findEditorLinkRanges } from "./editor-links";
import {
  formatInlineText,
  formatMarkdownLines,
  isInlineMarkdownFormat,
  type MarkdownFormat,
} from "./editor-formatting";
import { continueMarkdownMarkup, deleteMarkdownMarkupBackward, indentOrderedList, outdentOrderedList, normalizePastedListItem } from "./markdown-behavior";
import { formatToday } from "./date-time";
import {
  filterSlashCommands,
  getAiSlashContext,
  getSlashContext,
  SLASH_COMMANDS,
  type SlashCommand,
  type SlashContext,
} from "./editor-commands";
import { getWikiLinkContext } from "./wiki-link-completion";
import { getMentionContext, mentionRanges } from "./mentions";
import { markdownForUrlPaste } from "./url-paste";
import { nonWordRanges, spellingWords, type SpellChecker, type SpellingWord } from "./spelling-words";
import { imageAltWithWidth, parseMarkdownImage, type ParsedMarkdownImage } from "./markdown-image";
import { imageExtension } from "./vault/attachments";
import { parseMermaidFence } from "./mermaid-fence";
import { readEditorDiagramPalette, renderMermaidSvg } from "./mermaid-render";
import { setMermaidSvg } from "./mermaid-layout";
import { DEFAULT_MERMAID_SETTINGS, type MermaidSettings } from "./mermaid-settings";
import { type BorderStyle } from "./border-settings";
import { markdownFormatChanges } from "./markdown-format";
import { aiTargetExtension, aiTargetField, insertionBelow, setAiTarget } from "./editor-ai";
import {
  parseMarkdownTables,
  tableActionChange,
  tableAtPosition,
  tableFormatChanges,
  blockBreaks,
  tableInsertion,
  type TableAction,
} from "./markdown-table";

export const EDITOR_MODES = ["source", "live"] as const;
export type EditorMode = (typeof EDITOR_MODES)[number];

export function isEditorMode(value: string | null): value is EditorMode {
  return value !== null && (EDITOR_MODES as readonly string[]).includes(value);
}

/**
 * Where someone is in a note: the selection, and the line at the top of the view by where
 * it starts, scrolled `offset` pixels past its top.
 */
export interface EditorPlace {
  anchor: number;
  head: number;
  top: number;
  offset: number;
}

export interface MarkdownEditorOptions {
  onChange: (value: string) => void;
  onCursorChange: (line: number, column: number) => void;
  onActivateLink: (text: string) => void;
  onImageFile?: (file: File, placeholder: string) => Promise<string | null>;
  onImageError?: (message: string) => void;
  resolveImage?: (source: string) => string | null;
  getImageBorder?: () => BorderStyle;
  getMermaidSettings?: () => MermaidSettings;
  getWikiLinkSuggestions?: (query: string) => readonly string[];
  /** Remembered names to offer after an @, for what has been typed after it. */
  getMentionSuggestions?: (query: string) => readonly string[];
  /** The names a change has just @mentioned: each once the cursor has moved past it. */
  onMentions?: (names: readonly string[]) => void;
  getTodayText?: () => string;
  /**
   * Ask AI: about the selection, from typing / over it (it stays selected), or to write at
   * the cursor, from the /ai command (which is taken back out of the note).
   */
  onAskAi?: (about: "selection" | "cursor") => void;
  initialMode?: EditorMode;
  readOnly?: boolean;
  /** Underline unknown words with the app's spell checker, rather than leave it to the browser's. */
  spellCheck?: boolean;
}

const markdownHighlightStyle = HighlightStyle.define([
  { tag: tags.heading, color: "var(--text-heading)", fontWeight: "bold" },
  { tag: tags.strong, color: "var(--text-bold)", fontWeight: "bold" },
  { tag: tags.emphasis, color: "var(--text-italic)", fontStyle: "italic" },
  { tag: tags.strikethrough, textDecoration: "line-through" },
  { tag: tags.monospace, color: "var(--text-code)" },
  { tag: [tags.link, tags.url], color: "var(--text-link)", textDecoration: "underline" },
  { tag: tags.quote, color: "var(--text-quote)", fontStyle: "italic" },
  { tag: tags.list, color: "var(--text-list)" },
  { tag: tags.contentSeparator, color: "var(--text-hr)" },
]);

const editorTheme = EditorView.theme({
  "&": {
    height: "100%",
    color: "var(--text-editor)",
    backgroundColor: "var(--bg-editor)",
  },
  "&.cm-focused": {
    outline: "none",
    boxShadow: "inset 0 0 0 1px var(--border-divider)",
  },
  ".cm-scroller": {
    overflow: "auto",
    fontFamily: "var(--font-family)",
    fontSize: "20px",
    lineHeight: "1.45",
  },
  ".cm-content": {
    minHeight: "100%",
    padding: "6px 10px 80px",
    caretColor: "var(--text-caret)",
  },
  ".cm-line": {
    padding: "0",
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: "var(--text-caret)",
  },
  ".cm-gutters": {
    color: "var(--text-editor-dim)",
    backgroundColor: "var(--bg-editor)",
    borderRight: "1px solid var(--border-divider)",
  },
  ".cm-lineNumbers .cm-gutterElement": {
    minWidth: "3.5em",
    padding: "0 6px 0 4px",
  },
  "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground": {
    backgroundColor: "var(--text-editor-dim)",
  },
});

const CODE_NODE_NAMES = new Set(["InlineCode", "CodeBlock", "FencedCode"]);
// An @ in code, a URL, or HTML is not someone being mentioned.
const MENTION_EXCLUDED_NODE_NAMES = new Set([
  ...CODE_NODE_NAMES,
  "URL",
  "Autolink",
  "HTMLTag",
  "HTMLBlock",
  "CommentBlock",
]);
const URL_PASTE_LITERAL_NODE_NAMES = new Set([
  ...CODE_NODE_NAMES,
  "Autolink",
  "Link",
]);

function positionHasSyntaxAncestor(
  state: EditorState,
  position: number,
  bias: -1 | 1,
  nodeNames: ReadonlySet<string>,
): boolean {
  let node = syntaxTree(state).resolveInner(position, bias);
  while (true) {
    if (nodeNames.has(node.name)) return true;
    const parent = node.parent;
    if (!parent) return false;
    node = parent;
  }
}

function selectionRequiresLiteralUrlPaste(state: EditorState): boolean {
  const selection = state.selection.main;
  if (positionHasSyntaxAncestor(state, selection.from, 1, URL_PASTE_LITERAL_NODE_NAMES)) {
    return true;
  }
  return !selection.empty
    && positionHasSyntaxAncestor(
      state,
      Math.max(selection.from, selection.to - 1),
      -1,
      URL_PASTE_LITERAL_NODE_NAMES,
    );
}

function buildEditorLinkDecorations(view: EditorView): DecorationSet {
  const excludedRanges: Array<{ from: number; to: number }> = [];
  syntaxTree(view.state).iterate({
    enter(node) {
      if (!CODE_NODE_NAMES.has(node.name)) return true;
      excludedRanges.push({ from: node.from, to: node.to });
      return false;
    },
  });

  const builder = new RangeSetBuilder<Decoration>();
  const text = view.state.doc.toString();
  for (const link of findEditorLinkRanges(text, excludedRanges)) {
    builder.add(
      link.start,
      link.end,
      Decoration.mark({
        class: link.className,
        attributes: { "data-editor-link": link.text },
      }),
    );
  }
  return builder.finish();
}

const editorLinkDecorations = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildEditorLinkDecorations(view);
    }

    update(update: { docChanged: boolean; viewportChanged: boolean; view: EditorView }): void {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildEditorLinkDecorations(update.view);
      }
    }
  },
  { decorations: (value) => value.decorations },
);

class PreviewGlyph extends WidgetType {
  constructor(
    private readonly glyph: string,
    private readonly className: string,
    private readonly label?: string,
  ) {
    super();
  }

  eq(other: PreviewGlyph): boolean {
    return this.glyph === other.glyph
      && this.className === other.className
      && this.label === other.label;
  }

  toDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = this.className;
    span.textContent = this.glyph;
    span.setAttribute("aria-hidden", "true");
    if (this.label) span.title = this.label;
    return span;
  }
}

class TaskCheckboxWidget extends WidgetType {
  constructor(
    private readonly checked: boolean,
    private readonly markerFrom: number,
    private readonly readOnly: boolean,
  ) {
    super();
  }

  eq(other: TaskCheckboxWidget): boolean {
    return this.checked === other.checked && this.markerFrom === other.markerFrom
      && this.readOnly === other.readOnly;
  }

  toDOM(view: EditorView): HTMLElement {
    const control = document.createElement("label");
    control.className = "md-live-checkbox-control";
    control.title = this.checked ? "Mark task incomplete" : "Mark task complete";

    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = this.checked;
    input.disabled = view.state.readOnly;
    input.setAttribute("aria-label", control.title);
    input.addEventListener("change", () => {
      if (view.state.readOnly) return;
      const currentMarker = view.state.doc.sliceString(this.markerFrom, this.markerFrom + 3);
      if (!/^\[[ xX]\]$/.test(currentMarker)) {
        input.checked = this.checked;
        return;
      }
      view.dispatch({
        changes: {
          from: this.markerFrom + 1,
          to: this.markerFrom + 2,
          insert: input.checked ? "x" : " ",
        },
      });
    });

    const box = document.createElement("span");
    box.className = "md-live-checkbox-box";
    box.textContent = this.checked ? "[×]" : "[ ]";
    box.setAttribute("aria-hidden", "true");
    control.appendChild(input);
    control.appendChild(box);
    return control;
  }
}

const defaultImageSource = (source: string): string | null =>
  /^https?:\/\//i.test(source) || /^data:image\/(?:png|jpeg|gif|webp);base64,/i.test(source)
    ? source : null;
const imageResolverFacet = Facet.define<(source: string) => string | null, (source: string) => string | null>({
  combine: (values) => values[0] ?? defaultImageSource,
});
const mermaidSettingsFacet = Facet.define<() => MermaidSettings, () => MermaidSettings>({
  combine: (values) => values[0] ?? (() => DEFAULT_MERMAID_SETTINGS),
});
const imageBorderFacet = Facet.define<() => BorderStyle, () => BorderStyle>({
  combine: (values) => values[0] ?? (() => "none"),
});
/** Reports which table a live preview cell belongs to; the caret never enters one. */
const tableFocusFacet = Facet.define<(from: number) => void, (from: number) => void>({
  combine: (values) => values[0] ?? (() => {}),
});

class MermaidPreviewWidget extends WidgetType {
  constructor(
    private readonly from: number,
    private readonly codeFrom: number,
    private readonly code: string,
    private readonly settings: MermaidSettings,
    private readonly appearanceKey: string,
    private readonly readOnly: boolean,
  ) {
    super();
  }

  eq(other: MermaidPreviewWidget): boolean {
    return this.from === other.from && this.codeFrom === other.codeFrom
      && this.code === other.code && this.settings.theme === other.settings.theme
      && this.settings.border === other.settings.border
      && this.settings.align === other.settings.align
      && this.settings.width === other.settings.width
      && this.appearanceKey === other.appearanceKey
      && this.readOnly === other.readOnly;
  }

  toDOM(view: EditorView): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "md-live-mermaid";
    wrapper.dataset.mermaidTheme = this.settings.theme;
    wrapper.dataset.mermaidBorder = this.settings.border;
    wrapper.dataset.mermaidAlign = this.settings.align;
    wrapper.dataset.mermaidWidth = this.settings.width;
    if (this.settings.border === "double") wrapper.setAttribute("box-", "double");
    const toolbar = document.createElement("div");
    toolbar.className = "md-live-mermaid-toolbar";
    const label = document.createElement("span");
    label.textContent = "MERMAID";
    const edit = document.createElement("button");
    edit.type = "button";
    edit.textContent = "EDIT SOURCE";
    edit.setAttribute("aria-label", "Edit Mermaid source");
    edit.addEventListener("click", (event) => {
      event.preventDefault();
      view.dispatch({ selection: { anchor: this.codeFrom }, scrollIntoView: true });
      view.focus();
    });
    toolbar.append(label);
    if (!view.state.readOnly) toolbar.append(edit);
    const canvas = document.createElement("div");
    canvas.className = "md-live-mermaid-canvas";
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", "Mermaid diagram");
    canvas.textContent = "Rendering diagram…";
    wrapper.append(toolbar, canvas);
    const palette = readEditorDiagramPalette();
    void renderMermaidSvg(this.code, this.settings, palette).then((svg) => {
      if (wrapper.isConnected) setMermaidSvg(wrapper, canvas, svg);
    }).catch((error: unknown) => {
      if (!wrapper.isConnected) return;
      canvas.classList.add("md-live-mermaid-error");
      canvas.setAttribute("role", "alert");
      canvas.textContent = error instanceof Error ? `Diagram error: ${error.message}` : "Could not render diagram.";
    });
    return wrapper;
  }

  ignoreEvent(): boolean {
    return true;
  }
}

class ImagePreviewWidget extends WidgetType {
  constructor(
    private readonly image: ParsedMarkdownImage,
    private readonly markdown: string,
    private readonly resolvedSource: string | null,
    private readonly standalone: boolean,
    private readonly border: BorderStyle,
    private readonly readOnly: boolean,
  ) {
    super();
  }

  eq(other: ImagePreviewWidget): boolean {
    return this.image.from === other.image.from
      && this.markdown === other.markdown
      && this.resolvedSource === other.resolvedSource
      && this.standalone === other.standalone
      && this.border === other.border
      && this.readOnly === other.readOnly;
  }

  toDOM(view: EditorView): HTMLElement {
    const wrapper = document.createElement("span");
    wrapper.className = "md-live-image-wrap";
    if (this.standalone) wrapper.classList.add("md-live-image-standalone");
    if (!this.resolvedSource) {
      wrapper.classList.add("md-live-image-missing");
      wrapper.textContent = this.image.src.startsWith("turbonotes-pending:")
        ? "Uploading image…" : `Image unavailable: ${this.image.alt || this.image.src}`;
      return wrapper;
    }
    if (this.border === "double") wrapper.setAttribute("box-", "double");
    const image = document.createElement("img");
    image.className = "md-live-image";
    image.src = this.resolvedSource;
    image.alt = this.image.alt;
    image.draggable = false;
    image.loading = "lazy";
    if (this.image.width) image.style.width = `${this.image.width}px`;
    wrapper.append(image);
    if (view.state.readOnly) return wrapper;

    const handle = document.createElement("span");
    handle.className = "md-live-image-resize";
    handle.tabIndex = 0;
    handle.setAttribute("role", "slider");
    handle.setAttribute("aria-label", `Resize image ${this.image.alt || "image"}`);
    handle.setAttribute("aria-valuemin", "48");
    handle.setAttribute("aria-valuemax", "2000");
    handle.setAttribute("aria-valuenow", String(this.image.width ?? 240));
    handle.title = "Drag to resize image";
    handle.textContent = "↘";
    wrapper.append(handle);

    const currentWidth = (): number =>
      (this.image.width ?? Math.round(image.getBoundingClientRect().width)) || 240;
    const clampWidth = (width: number): number => Math.max(48, Math.min(2000, Math.round(width)));
    const commitWidth = (width: number): void => {
      const next = clampWidth(width);
      if (next === this.image.width
        || view.state.doc.sliceString(this.image.from, this.image.to) !== this.markdown) return;
      view.dispatch({
        changes: {
          from: this.image.altFrom,
          to: this.image.altTo,
          insert: imageAltWithWidth(this.image.rawAlt, next),
        },
        annotations: Transaction.userEvent.of("input"),
      });
    };
    let resizing: { pointerId: number; startX: number; width: number } | null = null;
    handle.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      event.stopPropagation();
      handle.setPointerCapture(event.pointerId);
      resizing = { pointerId: event.pointerId, startX: event.clientX, width: currentWidth() };
    });
    handle.addEventListener("pointermove", (event) => {
      if (!resizing || event.pointerId !== resizing.pointerId) return;
      const width = clampWidth(resizing.width + event.clientX - resizing.startX);
      image.style.width = `${width}px`;
      handle.setAttribute("aria-valuenow", String(width));
    });
    handle.addEventListener("pointerup", (event) => {
      if (!resizing || event.pointerId !== resizing.pointerId) return;
      const width = clampWidth(resizing.width + event.clientX - resizing.startX);
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      resizing = null;
      event.preventDefault();
      commitWidth(width);
    });
    handle.addEventListener("pointercancel", () => { resizing = null; });
    handle.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      event.stopPropagation();
      commitWidth(currentWidth() + (event.key === "ArrowRight" ? 1 : -1) * (event.shiftKey ? 100 : 20));
    });
    return wrapper;
  }

  ignoreEvent(): boolean {
    return true;
  }
}

interface TableSyntaxNode {
  name: string;
  from: number;
  to: number;
  firstChild: TableSyntaxNode | null;
  nextSibling: TableSyntaxNode | null;
}

interface PreviewTableCell {
  from: number;
  to: number;
  node: TableSyntaxNode;
}

interface PreviewTableRow {
  cells: PreviewTableCell[];
  to: number;
}

function tableCells(row: TableSyntaxNode): PreviewTableCell[] {
  const children = [...nodeChildren(row)];
  const delimiters = children.filter((child) => child.name === "TableDelimiter");
  const boundaries = [row.from, ...delimiters.flatMap((delimiter) => [delimiter.from, delimiter.to]), row.to];
  const cells: PreviewTableCell[] = [];
  for (let index = 0; index < boundaries.length - 1; index += 2) {
    const from = boundaries[index]!;
    const to = boundaries[index + 1]!;
    if (from === to) continue;
    const parsed = children.find((child) => child.name === "TableCell"
      && child.from >= from && child.to <= to);
    const node = parsed ?? {
      name: "TableCell",
      from,
      to: from,
      firstChild: null,
      nextSibling: null,
    };
    cells.push({ from: node.from, to: node.to, node });
  }
  return cells;
}

function appendInlineMarkdown(
  parent: HTMLElement,
  node: TableSyntaxNode,
  source: string,
): void {
  if (node.name === "Escape") {
    parent.append(document.createTextNode(source.slice(node.from + 1, node.to)));
    return;
  }

  const tag = node.name === "StrongEmphasis" ? "strong"
    : node.name === "Emphasis" ? "em"
      : node.name === "Strikethrough" ? "del"
        : node.name === "InlineCode" ? "code"
          : node.name === "URL" ? "span" : null;
  const container = tag ? document.createElement(tag) : parent;
  if (node.name === "URL") container.className = "md-live-table-url";
  if (node.name === "Link") {
    const labelEnd = linkLabelEnd(node, source);
    const urlNode = [...nodeChildren(node)].find((child) => child.name === "URL"
      && (labelEnd === null || child.from > labelEnd));
    const rawUrl = urlNode ? source.slice(urlNode.from, urlNode.to) : "";
    if (/^(?:https?:|mailto:)/i.test(rawUrl)) {
      const anchor = document.createElement("a");
      anchor.href = rawUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.className = "md-live-table-link";
      anchor.addEventListener("click", (event) => {
        if (!event.metaKey && !event.ctrlKey) event.preventDefault();
      });
      renderInlineChildren(anchor, node, source);
      parent.append(anchor);
      return;
    }
  }
  if (node.name === "Image") {
    const alt = source.slice(node.from, node.to).match(/^!\[([^\]]*)\]/)?.[1] ?? "image";
    parent.append(document.createTextNode(alt));
    return;
  }
  renderInlineChildren(container, node, source);
  if (tag) parent.append(container);
}

function* nodeChildren(node: TableSyntaxNode): Generator<TableSyntaxNode> {
  for (let child = node.firstChild; child; child = child.nextSibling) yield child;
}

function linkLabelEnd(node: TableSyntaxNode, source: string): number | null {
  for (const child of nodeChildren(node)) {
    if (child.name === "LinkMark" && source.slice(child.from, child.to) === "]") {
      return child.from;
    }
  }
  return null;
}

/**
 * Wraps a cell editor in a box holding the same raw text, so the column keeps the width
 * and height that text needs. The textarea only overlays that box and never sizes the
 * table itself, which would make every column jump the moment a cell is clicked.
 */
function tableCellEditor(
  input: HTMLTextAreaElement,
): { editor: HTMLElement; syncSize: () => void } {
  const editor = document.createElement("span");
  editor.className = "md-live-table-editor";
  const sizer = document.createTextNode(input.value);
  editor.append(sizer, input);
  return { editor, syncSize: () => { sizer.data = input.value; } };
}

function renderInlineChildren(parent: HTMLElement, node: TableSyntaxNode, source: string): void {
  let cursor = node.from;
  const labelEnd = node.name === "Link" ? linkLabelEnd(node, source) : null;
  for (const child of nodeChildren(node)) {
    if (child.from > cursor) parent.append(document.createTextNode(source.slice(cursor, child.from)));
    const hiddenUrl = child.name === "URL" && node.name === "Link"
      && labelEnd !== null && child.from > labelEnd;
    if (!/^(?:EmphasisMark|StrikethroughMark|CodeMark|LinkMark)$/.test(child.name)
      && !hiddenUrl) {
      appendInlineMarkdown(parent, child, source);
    }
    cursor = child.to;
  }
  if (cursor < node.to) parent.append(document.createTextNode(source.slice(cursor, node.to)));
}

function focusRenderedTableCell(
  view: EditorView,
  from: number,
  row: number,
  column: number,
  caret?: number | "start" | "end",
): void {
  queueMicrotask(() => {
    const current = view.contentDOM.querySelector<HTMLElement>(
      `.md-live-table-wrap[data-table-from="${from}"] `
      + `[data-table-row="${row}"][data-table-col="${column}"]`,
    );
    current?.dispatchEvent(new MouseEvent("pointerdown", {
      bubbles: true,
      cancelable: true,
    }));
    if (caret === undefined) return;
    const input = current?.querySelector<HTMLTextAreaElement>(".md-live-table-input");
    if (!input) return;
    const position = caret === "start" ? 0 : caret === "end"
      ? input.value.length : Math.min(caret, input.value.length);
    input.setSelectionRange(position, position);
  });
}

/**
 * Table data for a rendered widget, shared with the event handlers its DOM carries.
 * `updateDOM` receives the previously rendered widget rather than the one that built
 * the DOM, so refreshed data has to land somewhere both can reach.
 */
interface TablePreviewState {
  source: string;
  header: PreviewTableRow;
  rows: PreviewTableRow[];
  alignments: string[];
}

const tablePreviewStates = new WeakMap<HTMLElement, TablePreviewState>();

/**
 * Columns widened to fit their heading on one line by a double-click on the column's edge:
 * the headings, by where their table starts. They last while the note stays open.
 */
const fitTableColumn = StateEffect.define<{ table: number; heading: string }>();
const fittedTableColumns = StateField.define<ReadonlyMap<number, ReadonlySet<string>>>({
  create: () => new Map(),
  update(fitted, transaction) {
    if (!transaction.docChanged && !transaction.effects.some((effect) => effect.is(fitTableColumn))) return fitted;
    const next = new Map<number, ReadonlySet<string>>();
    for (const [table, headings] of fitted) {
      const from = transaction.changes.mapPos(table, 1, MapMode.TrackDel);
      if (from !== null) next.set(from, headings);
    }
    for (const effect of transaction.effects) {
      if (!effect.is(fitTableColumn)) continue;
      const { table, heading } = effect.value;
      next.set(table, new Set([...next.get(table) ?? [], heading]));
    }
    return next;
  },
});

const isFittedHeading = (view: EditorView, table: number, heading: string): boolean =>
  view.state.field(fittedTableColumns, false)?.get(table)?.has(heading.trim()) ?? false;

class TablePreviewWidget extends WidgetType {
  constructor(
    private readonly source: string,
    private readonly from: number,
    private readonly markdown: string,
    private readonly header: PreviewTableRow,
    private readonly rows: PreviewTableRow[],
    private readonly alignments: string[],
    private readonly readOnly: boolean,
  ) {
    super();
  }

  eq(other: TablePreviewWidget): boolean {
    return this.from === other.from && this.markdown === other.markdown
      && this.readOnly === other.readOnly;
  }

  updateDOM(dom: HTMLElement, view: EditorView, previous: TablePreviewWidget): boolean {
    const state = tablePreviewStates.get(dom);
    // Read-only, a table has no handles or editors, so a change either way draws it again.
    if (!state || this.from !== previous.from || this.readOnly !== previous.readOnly
      || this.header.cells.length !== state.header.cells.length
      || this.rows.length !== state.rows.length) return false;
    const cells = dom.querySelectorAll<HTMLElement>(".md-live-table-cell");
    if (cells.length !== (this.rows.length + 1) * this.header.cells.length) return false;
    state.source = this.source;
    state.header = this.header;
    state.rows = this.rows;
    state.alignments = this.alignments;
    cells.forEach((element) => {
      const row = Number(element.dataset.tableRow);
      const column = Number(element.dataset.tableCol);
      const cell = row === -1 ? this.header.cells[column] : this.rows[row]?.cells[column];
      element.dataset.cellRaw = cell ? this.source.slice(cell.from, cell.to) : "";
      element.style.textAlign = this.alignments[column] ?? "";
      if (row === -1)
        element.classList.toggle("md-live-table-fit", isFittedHeading(view, this.from, element.dataset.cellRaw));
      // A cell being edited keeps its textarea and the value typed into it.
      if (element.querySelector(".md-live-table-input")) return;
      const content = element.querySelector<HTMLElement>(".md-live-table-cell-content");
      if (!content) return;
      content.replaceChildren();
      if (cell) renderInlineChildren(content, cell.node, this.source);
    });
    return true;
  }

  toDOM(view: EditorView): HTMLElement {
    const state: TablePreviewState = {
      source: this.source,
      header: this.header,
      rows: this.rows,
      alignments: this.alignments,
    };
    const wrapper = document.createElement("div");
    wrapper.className = "md-live-table-wrap";
    wrapper.dataset.tableFrom = String(this.from);
    tablePreviewStates.set(wrapper, state);
    const table = document.createElement("table");
    table.className = "md-live-table";
    table.style.setProperty("--md-table-column-share", `${100 / this.header.cells.length}cqw`);
    table.setAttribute("divide-", "both");
    const head = document.createElement("thead");
    const body = document.createElement("tbody");
    const renderRow = (row: PreviewTableRow, parent: HTMLElement, rowIndex: number): void => {
      const tr = document.createElement("tr");
      tr.dataset.tableRow = String(rowIndex);
      for (let index = 0; index < this.header.cells.length; index++) {
        const cell = row.cells[index] ?? {
          from: row.to,
          to: row.to,
          node: {
            name: "TableCell",
            from: row.to,
            to: row.to,
            firstChild: null,
            nextSibling: null,
          },
        };
        const element = document.createElement(rowIndex === -1 ? "th" : "td");
        element.style.textAlign = this.alignments[index] ?? "";
        element.dataset.tableRow = String(rowIndex);
        element.dataset.tableCol = String(index);
        element.dataset.cellRaw = this.source.slice(cell.from, cell.to);
        element.className = "md-live-table-cell";
        if (rowIndex === -1)
          element.classList.toggle("md-live-table-fit", isFittedHeading(view, this.from, element.dataset.cellRaw));
        if (!view.state.readOnly) element.tabIndex = 0;
        const content = document.createElement("span");
        content.className = "md-live-table-cell-content";
        renderInlineChildren(content, cell.node, this.source);
        const edge = document.createElement("span");
        edge.className = "md-live-table-col-edge";
        edge.title = "Double-click to fit the column to its heading";
        edge.setAttribute("aria-hidden", "true");
        element.append(content, edge);
        if (!view.state.readOnly && (rowIndex === -1 || index === 0)) {
          const handle = document.createElement("span");
          handle.className = `md-live-table-drag md-live-table-drag-${rowIndex === -1 ? "column" : "row"}`;
          handle.tabIndex = 0;
          handle.setAttribute("role", "button");
          handle.setAttribute("aria-label", rowIndex === -1
            ? `Drag column ${index + 1} to reorder`
            : `Drag row ${rowIndex + 1} to reorder`);
          handle.textContent = rowIndex === -1 ? "⋮" : "⋮⋮";
          handle.dataset.dragType = rowIndex === -1 ? "column" : "row";
          handle.dataset.dragIndex = String(rowIndex === -1 ? index : rowIndex);
          element.append(handle);
        }
        if (!view.state.readOnly && rowIndex === -1) {
          const remove = document.createElement("button");
          remove.type = "button";
          remove.className = "md-live-table-delete-column";
          remove.setAttribute("aria-label", `Delete column ${index + 1}`);
          remove.disabled = this.header.cells.length <= 1;
          remove.title = remove.disabled ? "Keep at least one column" : `Delete column ${index + 1}`;
          remove.textContent = "×";
          element.append(remove);
        }
        if (!view.state.readOnly && rowIndex === -1 && index === this.header.cells.length - 1) {
          const add = document.createElement("button");
          add.type = "button";
          add.className = "md-live-table-add-column";
          add.setAttribute("aria-label", "Add table column");
          add.title = "Add column";
          add.textContent = "+";
          element.append(add);
        }
        tr.append(element);
      }
      parent.append(tr);
    };
    renderRow(this.header, head, -1);
    this.rows.forEach((row, index) => renderRow(row, body, index));
    table.append(head, body);
    wrapper.append(table);
    // As in a spreadsheet, double-clicking a column's right edge fits the column to its heading.
    wrapper.addEventListener("pointerdown", (event) => {
      if (event.target instanceof Element && event.target.closest(".md-live-table-col-edge")) event.preventDefault();
    });
    wrapper.addEventListener("dblclick", (event) => {
      const edge = event.target instanceof Element ? event.target.closest(".md-live-table-col-edge") : null;
      const column = edge?.closest<HTMLElement>(".md-live-table-cell")?.dataset.tableCol;
      const heading = column === undefined ? null : head.querySelector<HTMLElement>(`th[data-table-col="${column}"]`);
      if (!heading) return;
      event.preventDefault();
      heading.classList.add("md-live-table-fit");
      view.dispatch({ effects: fitTableColumn.of({ table: this.from, heading: (heading.dataset.cellRaw ?? "").trim() }) });
    });
    if (view.state.readOnly) {
      // Cells cannot be opened here, but clicking one still says which table it is.
      wrapper.addEventListener("pointerdown", () => {
        view.state.facet(tableFocusFacet)(this.from);
      });
      return wrapper;
    }

    const applyAction = (action: TableAction): void => {
      const change = tableActionChange(view.state.doc.toString(), this.from, action);
      if (!change) return;
      view.dispatch({
        changes: change,
        annotations: action.type === "delete-column"
          ? [Transaction.userEvent.of("delete"), isolateHistory.of("full")]
          : Transaction.userEvent.of("input"),
      });
    };
    let activeInput: HTMLTextAreaElement | null = null;
    let activeEditor: HTMLElement | null = null;
    let activeContent: HTMLElement | null = null;
    let activeRow = -1;
    let activeColumn = 0;
    const focusCell = (row: number, column: number, caret?: number | "start" | "end"): void => {
      focusRenderedTableCell(view, this.from, row, column, caret);
    };
    const finish = (
      next?: { row: number; column: number; caret?: number | "start" | "end" },
      addRow = false,
    ): void => {
      const input = activeInput;
      if (!input) {
        if (addRow) applyAction({ type: "add-row" });
        if (next) focusCell(next.row, next.column, next.caret);
        return;
      }
      activeInput = null;
      const row = activeRow;
      const column = activeColumn;
      const original = input.dataset.original ?? "";
      const value = input.value;
      (activeEditor ?? input).remove();
      activeEditor = null;
      if (activeContent) {
        activeContent.replaceChildren();
        const cell = row === -1 ? state.header.cells[column] : state.rows[row]?.cells[column];
        if (cell) renderInlineChildren(activeContent, cell.node, state.source);
        activeContent.hidden = false;
      }
      activeContent = null;
      if (value !== original) applyAction({ type: "edit", row, column, value });
      if (addRow) applyAction({ type: "add-row" });
      if (next) focusCell(next.row, next.column, next.caret);
    };
    const leaveTable = (direction: "before" | "after"): void => {
      finish();
      const table = parseMarkdownTables(view.state.doc.toString())
        .find((candidate) => candidate.from === this.from);
      if (!table) return;
      const doc = view.state.doc;
      if (direction === "before") {
        const line = doc.lineAt(table.from);
        if (line.number > 1 && doc.sliceString(0, line.from).endsWith("\n\n")) {
          view.dispatch({ selection: { anchor: line.from - 2 }, scrollIntoView: true });
        } else if (line.number > 1) {
          view.dispatch({
            changes: { from: line.from, insert: "\n" },
            selection: { anchor: doc.line(line.number - 1).to },
            scrollIntoView: true,
            annotations: Transaction.userEvent.of("input"),
          });
        } else {
          view.dispatch({
            changes: { from: 0, insert: "\n\n" },
            selection: { anchor: 0 },
            scrollIntoView: true,
            annotations: Transaction.userEvent.of("input"),
          });
        }
      } else {
        const after = doc.sliceString(table.to);
        if (after.startsWith("\n\n")) {
          view.dispatch({ selection: { anchor: table.to + 2 }, scrollIntoView: true });
        } else if (after.startsWith("\n")) {
          view.dispatch({
            changes: { from: table.to + 1, insert: "\n" },
            selection: { anchor: table.to + 2 },
            scrollIntoView: true,
            annotations: Transaction.userEvent.of("input"),
          });
        } else {
          view.dispatch({
            changes: { from: table.to, insert: "\n\n" },
            selection: { anchor: table.to + 2 },
            scrollIntoView: true,
            annotations: Transaction.userEvent.of("input"),
          });
        }
      }
      view.focus();
    };
    const navigate = (
      row: number,
      column: number,
      key: string,
      caret: number,
    ): void => {
      const lastColumn = state.header.cells.length - 1;
      const lastRow = state.rows.length - 1;
      if (key === "ArrowLeft") {
        if (column > 0) finish({ row, column: column - 1, caret: "end" });
        else if (row > -1) finish({ row: row - 1, column: lastColumn, caret: "end" });
        else leaveTable("before");
      } else if (key === "ArrowRight") {
        if (column < lastColumn) finish({ row, column: column + 1, caret: "start" });
        else if (row < lastRow) finish({ row: row + 1, column: 0, caret: "start" });
        else leaveTable("after");
      } else if (key === "ArrowUp") {
        if (row > -1) finish({ row: row - 1, column, caret });
        else leaveTable("before");
      } else if (key === "ArrowDown") {
        if (row < lastRow) finish({ row: row + 1, column, caret });
        else leaveTable("after");
      }
    };
    const activate = (cell: HTMLElement): void => {
      const row = Number(cell.dataset.tableRow);
      const column = Number(cell.dataset.tableCol);
      view.state.facet(tableFocusFacet)(this.from);
      if (activeInput) {
        if (row === activeRow && column === activeColumn) {
          activeInput.focus({ preventScroll: true });
          return;
        }
        finish({ row, column });
        return;
      }
      const content = cell.querySelector<HTMLElement>(".md-live-table-cell-content");
      if (!content) return;
      const input = document.createElement("textarea");
      input.rows = 1;
      input.className = "md-live-table-input";
      input.value = cell.dataset.cellRaw ?? "";
      const { editor, syncSize } = tableCellEditor(input);
      const initialValue = input.value;
      input.dataset.original = input.value;
      input.setAttribute("aria-label", `${row === -1 ? "Header" : `Row ${row + 1}`}, column ${column + 1}`);
      input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          activeInput = null;
          activeEditor = null;
          editor.remove();
          content.hidden = false;
          activeContent = null;
          if (input.value !== initialValue) {
            applyAction({ type: "edit", row, column, value: initialValue });
          }
          view.focus();
          return;
        }
        if (/^Arrow(?:Left|Right|Up|Down)$/.test(event.key)
          && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
          && !event.isComposing) {
          const start = input.selectionStart ?? 0;
          const end = input.selectionEnd ?? start;
          const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
          if (!horizontal || (start === end
            && (event.key === "ArrowLeft" ? start === 0 : end === input.value.length))) {
            event.preventDefault();
            event.stopPropagation();
            navigate(row, column, event.key, start);
            return;
          }
        }
        if (event.key !== "Tab" && event.key !== "Enter") return;
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey || event.altKey)) return;
        event.preventDefault();
        const lastColumn = state.header.cells.length - 1;
        if (event.key === "Tab" && event.shiftKey) {
          const previous = column > 0
            ? { row, column: column - 1 }
            : row > -1 ? { row: row - 1, column: lastColumn } : null;
          finish(previous ?? undefined);
          return;
        }
        if (event.key === "Enter") {
          const nextRow = row + 1;
          finish({ row: nextRow, column }, nextRow >= state.rows.length);
          return;
        }
        const next = column < lastColumn
          ? { row, column: column + 1 }
          : { row: row + 1, column: 0 };
        finish(next, next.row >= state.rows.length);
      });
      input.addEventListener("blur", () => {
        if (activeInput === input) finish();
      });
      let composing = false;
      const saveInput = (): void => {
        if (composing || activeInput !== input) return;
        applyAction({ type: "edit", row, column, value: input.value });
        input.dataset.original = input.value;
      };
      input.addEventListener("input", () => {
        syncSize();
        saveInput();
      });
      input.addEventListener("compositionstart", () => { composing = true; });
      input.addEventListener("compositionend", () => {
        composing = false;
        syncSize();
        saveInput();
      });
      activeInput = input;
      activeEditor = editor;
      activeContent = content;
      activeRow = row;
      activeColumn = column;
      content.hidden = true;
      cell.insertBefore(editor, content.nextSibling);
      input.focus({ preventScroll: true });
      input.select();
    };
    let dragging: { type: "row" | "column"; index: number; pointerId: number } | null = null;
    const dragTarget = (event: PointerEvent): HTMLElement | null => {
      if (!dragging) return null;
      const point = document.elementFromPoint(event.clientX, event.clientY);
      const target = point?.closest<HTMLElement>(dragging.type === "row"
        ? "tbody tr[data-table-row]" : "thead th[data-table-col]");
      return target && wrapper.contains(target) ? target : null;
    };
    const clearDrag = (): void => {
      dragging = null;
      wrapper.querySelectorAll(".md-live-table-drop-target")
        .forEach((element) => element.classList.remove("md-live-table-drop-target"));
    };
    wrapper.addEventListener("pointerdown", (event) => {
      const handle = event.target instanceof Element
        ? event.target.closest<HTMLElement>(".md-live-table-drag") : null;
      if (handle) {
        event.preventDefault();
        handle.setPointerCapture(event.pointerId);
        dragging = {
          type: handle.dataset.dragType as "row" | "column",
          index: Number(handle.dataset.dragIndex),
          pointerId: event.pointerId,
        };
        return;
      }
      if ((event.metaKey || event.ctrlKey)
        && event.target instanceof Element
        && event.target.closest("a")) return;
      if (event.target instanceof Element
        && event.target.closest(".md-live-table-editor, .md-live-table-add-column, .md-live-table-delete-column, .md-live-table-col-edge")) return;
      const target = event.target instanceof Element
        ? event.target.closest<HTMLElement>(".md-live-table-cell")
        : null;
      if (!target) return;
      event.preventDefault();
      activate(target);
    });
    wrapper.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      const remove = event.target.closest<HTMLButtonElement>(".md-live-table-delete-column");
      if (remove) {
        event.preventDefault();
        if (remove.disabled) return;
        const column = Number(remove.closest<HTMLElement>("th")!.dataset.tableCol);
        const nextColumn = Math.min(column, state.header.cells.length - 2);
        finish();
        applyAction({ type: "delete-column", column });
        queueMicrotask(() => {
          view.contentDOM.querySelector<HTMLElement>(
            `.md-live-table-wrap[data-table-from="${this.from}"] thead th[data-table-col="${nextColumn}"]`,
          )?.focus({ preventScroll: true });
        });
        return;
      }
      if (!event.target.closest(".md-live-table-add-column")) return;
      event.preventDefault();
      const columns = state.header.cells.length;
      finish();
      applyAction({ type: "add-column" });
      focusCell(-1, columns);
    });
    wrapper.addEventListener("pointermove", (event) => {
      if (!dragging || event.pointerId !== dragging.pointerId) return;
      const target = dragTarget(event);
      wrapper.querySelectorAll(".md-live-table-drop-target")
        .forEach((element) => element.classList.remove("md-live-table-drop-target"));
      target?.classList.add("md-live-table-drop-target");
    });
    wrapper.addEventListener("pointerup", (event) => {
      if (!dragging || event.pointerId !== dragging.pointerId) return;
      const { type, index } = dragging;
      const target = dragTarget(event);
      if (event.target instanceof Element && event.target.hasPointerCapture?.(event.pointerId)) {
        event.target.releasePointerCapture(event.pointerId);
      }
      clearDrag();
      if (!target) return;
      event.preventDefault();
      finish();
      applyAction(type === "row"
        ? { type: "move-row", from: index, to: Number(target.dataset.tableRow) }
        : { type: "move-column", from: index, to: Number(target.dataset.tableCol) });
    });
    wrapper.addEventListener("pointercancel", clearDrag);
    wrapper.addEventListener("keydown", (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.isComposing
        && event.key.toLowerCase() === "z") {
        event.preventDefault();
        event.stopPropagation();
        finish();
        if ((event.shiftKey ? redo : undo)(view)) view.focus();
        return;
      }
      if (event.target instanceof HTMLElement && event.target.matches(".md-live-table-cell")) {
        if (/^Arrow(?:Left|Right|Up|Down)$/.test(event.key)
          && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
          event.preventDefault();
          navigate(Number(event.target.dataset.tableRow), Number(event.target.dataset.tableCol), event.key, 0);
          return;
        }
        if (event.key === "Enter" || event.key === "F2") {
          event.preventDefault();
          activate(event.target);
          return;
        }
      }
      const handle = event.target instanceof Element
        ? event.target.closest<HTMLElement>(".md-live-table-drag") : null;
      if (!handle || !event.altKey) return;
      const type = handle.dataset.dragType;
      const index = Number(handle.dataset.dragIndex);
      const delta = type === "row"
        ? event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0
        : event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
      if (!delta) return;
      event.preventDefault();
      finish();
      applyAction(type === "row"
        ? { type: "move-row", from: index, to: index + delta }
        : { type: "move-column", from: index, to: index + delta });
    });
    return wrapper;
  }

  ignoreEvent(): boolean {
    return true;
  }
}

/** Live Preview's bullet and task checkbox, in the line's own characters (see .md-live-bullet and .md-live-checkbox-control). */
const LIVE_BULLET_WIDTH = 0.72;
const LIVE_CHECKBOX_WIDTH = 3.35;

/**
 * The first line of a list item, marked so its wrapped lines hang under the item's first
 * letter rather than under its marker. Every editor font is monospaced, so the indent is in
 * characters; in Live Preview a drawn bullet or checkbox stands in for its source.
 */
function hangingListLine(state: EditorState, mark: { from: number; to: number }, preview: boolean): Range<Decoration> {
  const line = state.doc.lineAt(mark.from);
  const spacing = /^[\t ]+(?:(\[[ xX]\])[\t ]*)?/.exec(state.doc.sliceString(mark.to, line.to));
  let indent = countColumn(state.doc.sliceString(line.from, mark.to + (spacing?.[0].length ?? 0)), state.tabSize);
  // A task's - is hidden and its [ ] drawn as a checkbox; a bullet's - is drawn smaller.
  if (preview && /^[-+*]$/.test(state.doc.sliceString(mark.from, mark.to)))
    indent += spacing?.[1] ? LIVE_CHECKBOX_WIDTH - 4 : LIVE_BULLET_WIDTH - 1;
  return Decoration.line({
    class: "md-list-line",
    attributes: { style: `--md-list-indent: ${Math.round(indent * 100) / 100}ch` },
  }).range(line.from);
}

/** Source has no drawn markers, so its list lines hang under the text after the Markdown. */
function buildSourceListLines(state: EditorState): DecorationSet {
  const lines: Array<Range<Decoration>> = [];
  syntaxTree(state).iterate({
    enter(node) {
      if (node.name === "ListMark") lines.push(hangingListLine(state, node, false));
    },
  });
  return Decoration.set(lines, true);
}

const sourceListLines = StateField.define<DecorationSet>({
  create: buildSourceListLines,
  update: (lines, transaction) => transaction.docChanged
    || syntaxTree(transaction.startState) !== syntaxTree(transaction.state)
    ? buildSourceListLines(transaction.state)
    : lines,
  provide: (field) => EditorView.decorations.from(field),
});

const INLINE_SOURCE_OWNERS: Record<string, readonly string[]> = {
  EmphasisMark: ["Emphasis", "StrongEmphasis"],
  StrikethroughMark: ["Strikethrough"],
  SubscriptMark: ["Subscript"],
  SuperscriptMark: ["Superscript"],
  CodeMark: ["InlineCode", "FencedCode"],
  CodeInfo: ["FencedCode"],
  LinkMark: ["Link", "Autolink", "LinkReference"],
  LinkLabel: ["Link"],
  LinkTitle: ["Link", "LinkReference"],
  URL: ["Link", "LinkReference"],
};

export function buildLivePreviewDecorations(state: EditorState): DecorationSet {
  const ranges: Array<Range<Decoration>> = [];
  let fullSource: string | null = null;
  const resolveImage = state.facet(imageResolverFacet);
  const getImageBorder = state.facet(imageBorderFacet);
  const getMermaidSettings = state.facet(mermaidSettingsFacet);
  const appearanceKey = typeof document === "undefined" ? ""
    : `${document.documentElement.dataset.colorTheme}|${document.documentElement.dataset.editorFont}`;
  const activeLines = new Set(
    state.readOnly ? []
      : state.selection.ranges.map((range) => state.doc.lineAt(range.head).number),
  );
  const lineIsActive = (position: number): boolean => (
    activeLines.has(state.doc.lineAt(Math.min(position, state.doc.length)).number)
  );
  const selectionTouches = (from: number, to: number, includeEnd = true): boolean => !state.readOnly
    && state.selection.ranges.some((range) => range.empty
      ? range.head >= from && (includeEnd ? range.head <= to : range.head < to)
      : range.from < to && range.to > from);
  const addLineClass = (position: number, className: string): void => {
    const line = state.doc.lineAt(Math.min(position, state.doc.length));
    ranges.push(Decoration.line({ class: className }).range(line.from));
  };

  syntaxTree(state).iterate({
    enter(node) {
      if (node.name === "Table") {
        let header: PreviewTableRow | null = null;
        let delimiter = "";
        const rows: PreviewTableRow[] = [];
        for (let child = node.node.firstChild; child; child = child.nextSibling) {
          if (child.name === "TableHeader") header = { cells: tableCells(child), to: child.to };
          else if (child.name === "TableDelimiter") {
            delimiter = state.doc.sliceString(child.from, child.to);
          } else if (child.name === "TableRow") rows.push({ cells: tableCells(child), to: child.to });
        }
        if (header) {
          const alignmentCells = delimiter.trim().replace(/^\|/, "").replace(/\|$/, "").split("|");
          const alignments = alignmentCells.map((cell) => {
            const marker = cell.trim();
            return marker.startsWith(":") && marker.endsWith(":") ? "center"
              : marker.startsWith(":") ? "left"
                : marker.endsWith(":") ? "right" : "";
          });
          ranges.push(Decoration.replace({
            widget: new TablePreviewWidget(
              fullSource ??= state.doc.toString(),
              node.from,
              state.doc.sliceString(node.from, node.to),
              header,
              rows,
              alignments,
              state.readOnly,
            ),
            block: true,
          }).range(node.from, node.to));
        }
        return false;
      }

      if (node.name === "Image") {
        const source = fullSource ??= state.doc.toString();
        const parsed = parseMarkdownImage(node.node, source);
        if (parsed) {
          const line = state.doc.lineAt(node.from);
          const standalone = !state.doc.sliceString(line.from, node.from).trim()
            && !state.doc.sliceString(node.to, line.to).trim();
          ranges.push(Decoration.replace({
            widget: new ImagePreviewWidget(
              parsed,
              source.slice(node.from, node.to),
              resolveImage(parsed.src) ?? defaultImageSource(parsed.src),
              standalone,
              getImageBorder(),
              state.readOnly,
            ),
          }).range(node.from, node.to));
        }
        return false;
      }

      const heading = /^(?:ATXHeading|SetextHeading)([1-6])$/.exec(node.name);
      if (heading) addLineClass(node.from, `md-live-heading md-live-heading-${heading[1]}`);

      if (node.name === "FencedCode" || node.name === "CodeBlock") {
        const fence = node.name === "FencedCode"
          ? parseMermaidFence(node.node, fullSource ??= state.doc.toString()) : null;
        const editingFence = fence && !state.readOnly && state.selection.ranges.some((range) =>
          range.from < fence.to && range.to > fence.from
          || range.empty && range.head >= fence.from && range.head < fence.to);
        if (fence && !editingFence) {
          ranges.push(Decoration.replace({
            widget: new MermaidPreviewWidget(
              fence.from, fence.codeFrom, fence.code, getMermaidSettings(), appearanceKey, state.readOnly,
            ),
            block: true,
          }).range(fence.from, fence.to));
          return false;
        }
        let line = state.doc.lineAt(node.from);
        while (line.from <= node.to) {
          ranges.push(Decoration.line({ class: "md-live-code-line" }).range(line.from));
          if (line.to >= node.to || line.number === state.doc.lines) break;
          line = state.doc.line(line.number + 1);
        }
        if (fence) return false;
      }

      const source = state.doc.sliceString(node.from, node.to);
      if (node.name === "Subscript" || node.name === "Superscript") {
        ranges.push(Decoration.mark({
          class: node.name === "Subscript" ? "md-live-subscript" : "md-live-superscript",
        }).range(node.from + 1, node.to - 1));
      }
      if (node.name === "ListItem") {
        const task = /^(?:\s*[-+*]\s+)\[([ xX])\]/.exec(source);
        if (task) {
          const checked = task[1]?.toLocaleLowerCase() === "x";
          if (checked) addLineClass(node.from, "md-live-task-complete");
          const bracketOffset = source.indexOf("[");
          const markerFrom = node.from + bracketOffset;
          if (!selectionTouches(node.from, markerFrom + 3)) {
            ranges.push(
              Decoration.replace({ widget: new TaskCheckboxWidget(checked, markerFrom, state.readOnly) })
                .range(markerFrom, markerFrom + 3),
            );
          }
        }
      }

      if (node.name === "ListMark") {
        const line = state.doc.lineAt(node.from);
        const taskPrefix = /^[\t ]+\[[ xX]\]/.exec(state.doc.sliceString(node.to, line.to));
        const revealed = selectionTouches(node.from, node.to + (taskPrefix?.[0].length ?? 0));
        ranges.push(hangingListLine(state, node, !revealed));
        if (revealed) return;
        if (/^[-+*]$/.test(source)) {
          ranges.push(taskPrefix
            ? Decoration.replace({}).range(node.from, node.to)
            : Decoration.replace({ widget: new PreviewGlyph("■", "md-live-bullet", "Bullet") })
              .range(node.from, node.to));
        } else {
          ranges.push(Decoration.mark({ class: "md-live-list-number" }).range(node.from, node.to));
        }
        return;
      }

      if (node.name === "HeaderMark") {
        const trailingSpace = state.doc.sliceString(node.to, node.to + 1) === " " ? 1 : 0;
        const line = state.doc.lineAt(node.from);
        const opening = /^#+$/.test(source)
          && !state.doc.sliceString(line.from, node.from).trim();
        if (lineIsActive(node.from)) {
          if (opening) ranges.push(Decoration.mark({ class: "md-live-heading-source" })
            .range(node.from, node.to + trailingSpace));
        } else {
          ranges.push(Decoration.replace({}).range(node.from, node.to + trailingSpace));
        }
        return;
      }

      const sourceOwners = INLINE_SOURCE_OWNERS[node.name];
      if (sourceOwners) {
        if (node.name === "URL" && node.node.parent?.name === "Autolink") return;
        let owner = node.node.parent;
        while (owner && !sourceOwners.includes(owner.name)) owner = owner.parent;
        if (node.name === "URL" && owner?.name === "Link") {
          const labelEnd = linkLabelEnd(owner, fullSource ??= state.doc.toString());
          if (labelEnd !== null && node.to <= labelEnd) return;
        }
        if (owner && !selectionTouches(owner.from, owner.to, false)) {
          ranges.push(Decoration.replace({}).range(node.from, node.to));
        }
        return;
      }

      if (node.name === "QuoteMark") {
        if (lineIsActive(node.from)) return;
        ranges.push(
          Decoration.replace({ widget: new PreviewGlyph("│", "md-live-quote-mark", "Quote") })
            .range(node.from, node.to),
        );
        addLineClass(node.from, "md-live-quote-line");
      } else if (node.name === "HorizontalRule") {
        if (lineIsActive(node.from)) return;
        ranges.push(
          Decoration.replace({ widget: new PreviewGlyph("────────────────", "md-live-rule") })
            .range(node.from, node.to),
        );
      }
    },
  });

  return Decoration.set(ranges, true);
}

const refreshLivePreview = StateEffect.define<void>();

interface CodeScrollBlock { from: number; to: number; left: number; columns: number; }
const codeBlockMode = Facet.define<boolean, boolean>({ combine: (values) => values.some(Boolean) });
const scrollCodeBlock = StateEffect.define<{ from: number; left: number }>();
let nextCodeScrollbarId = 0;

function codeLineInsets(line: HTMLElement | undefined): number {
  if (!line) return 0;
  const style = getComputedStyle(line);
  return [style.paddingLeft, style.paddingRight, style.borderLeftWidth, style.borderRightWidth]
    .reduce((total, value) => total + (parseFloat(value) || 0), 0);
}

class CodeBlockScrollbarWidget extends WidgetType {
  constructor(readonly from: number, readonly left: number) { super(); }
  eq(other: CodeBlockScrollbarWidget): boolean { return this.from === other.from && this.left === other.left; }
  toDOM(view: EditorView): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "md-code-scrollbar";
    bar.id = `md-code-scrollbar-${++nextCodeScrollbarId}`;
    bar.dataset.codeBlock = String(this.from);
    bar.setAttribute("role", "scrollbar");
    bar.setAttribute("aria-label", "Scroll code block horizontally");
    bar.setAttribute("aria-orientation", "horizontal");
    const track = document.createElement("div");
    track.className = "md-code-scrollbar-track";
    bar.append(track);
    bar.addEventListener("scroll", () => {
      const from = Number(bar.dataset.codeBlock);
      const block = view.state.field(codeBlockLineDecorations).blocks.find((item) => item.from === from);
      if (block && Math.abs(block.left - bar.scrollLeft) > 0.5)
        view.dispatch({ effects: scrollCodeBlock.of({ from, left: bar.scrollLeft }) });
    });
    bar.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      bar.scrollLeft = event.key === "Home" ? 0 : event.key === "End" ? bar.scrollWidth
        : bar.scrollLeft + (event.key === "ArrowLeft" ? -40 : 40);
    });
    return bar;
  }
  updateDOM(dom: HTMLElement): boolean {
    dom.dataset.codeBlock = String(this.from);
    dom.scrollLeft = this.left;
    return true;
  }
}

function buildCodeBlockLineDecorations(state: EditorState, offsets = new Map<number, number>()) {
  const ranges: Array<Range<Decoration>> = [];
  const blocks: CodeScrollBlock[] = [];
  let source: string | undefined;
  syntaxTree(state).iterate({
    enter(node) {
      if (node.name !== "FencedCode" && node.name !== "CodeBlock") return;
      if (state.facet(codeBlockMode) && node.name === "FencedCode") {
        const fence = parseMermaidFence(node.node, source ??= state.doc.toString());
        if (fence && !state.selection.ranges.some((range) =>
          range.from < fence.to && range.to > fence.from
          || range.empty && range.head >= fence.from && range.head < fence.to)) return false;
      }
      const left = offsets.get(node.from) ?? 0;
      let columns = 0;
      let line = state.doc.lineAt(node.from);
      while (line.from <= node.to) {
        let lineColumns = 0;
        for (const character of line.text)
          lineColumns += character === "\t" ? state.tabSize - lineColumns % state.tabSize : 1;
        columns = Math.max(columns, lineColumns);
        ranges.push(Decoration.line({
          class: "md-code-line",
          attributes: { "data-code-block": String(node.from), style: `--md-code-scroll-left: ${left}px` },
        }).range(line.from));
        if (line.length) ranges.push(Decoration.mark({ class: "md-code-scroll-text" }).range(line.from, line.to));
        if (line.to >= node.to || line.number === state.doc.lines) break;
        line = state.doc.line(line.number + 1);
      }
      blocks.push({ from: node.from, to: node.to, left, columns });
      ranges.push(Decoration.widget({
        widget: new CodeBlockScrollbarWidget(node.from, left), block: true, side: 1,
      }).range(line.to));
      return false;
    },
  });
  return { decorations: Decoration.set(ranges, true), blocks };
}

const codeBlockLineDecorations = StateField.define<ReturnType<typeof buildCodeBlockLineDecorations>>({
  create: (state) => buildCodeBlockLineDecorations(state),
  update(value, transaction) {
    if (transaction.docChanged || transaction.selection
      || syntaxTree(transaction.startState) !== syntaxTree(transaction.state)
      || transaction.startState.facet(codeBlockMode) !== transaction.state.facet(codeBlockMode)
      || transaction.effects.some((effect) => effect.is(refreshLivePreview) || effect.is(scrollCodeBlock))) {
      const offsets = new Map(value.blocks.map((block) => [transaction.changes.mapPos(block.from), block.left]));
      for (const effect of transaction.effects)
        if (effect.is(scrollCodeBlock)) offsets.set(effect.value.from, effect.value.left);
      return buildCodeBlockLineDecorations(transaction.state, offsets);
    }
    return value;
  },
  provide: (field) => EditorView.decorations.from(field, (value) => value.decorations),
});

const codeBlockScrolling = ViewPlugin.fromClass(class {
  private destroyed = false;
  private revealCaret = false;
  constructor(private readonly view: EditorView) { this.measure(); }
  update(update: ViewUpdate): void {
    if (update.transactions.some((transaction) => transaction.effects.some((effect) => effect.is(scrollCodeBlock))))
      this.revealCaret = false;
    else if (update.selectionSet) this.revealCaret = true;
    if (update.docChanged || update.selectionSet || update.viewportChanged || update.geometryChanged
      || update.startState.field(codeBlockLineDecorations) !== update.state.field(codeBlockLineDecorations)) this.measure();
  }
  private measure(): void {
    this.view.requestMeasure({
      key: this,
      read: (view) => {
        // With word wrap off the whole editor scrolls sideways, code included.
        const wrapping = !view.lineWrapping
          || view.dom.ownerDocument.documentElement.dataset.codeWordWrap === "true";
        const { blocks } = view.state.field(codeBlockLineDecorations);
        const measurements = blocks.flatMap((block) => {
          const { from } = block;
          const bar = view.contentDOM.querySelector<HTMLElement>(`.md-code-scrollbar[data-code-block="${from}"]`);
          const lines = [...view.contentDOM.querySelectorAll<HTMLElement>(`.md-code-line[data-code-block="${from}"]`)];
          if (!bar && !lines.length) return [];
          const width = bar?.clientWidth || lines[0]?.getBoundingClientRect().width || 0;
          const insets = codeLineInsets(lines[0]);
          const fullWidth = Math.max(width, block.columns * view.defaultCharacterWidth + insets,
            ...lines.map((line) => (line.querySelector<HTMLElement>(".md-code-scroll-text")?.scrollWidth ?? 0) + insets));
          const max = wrapping ? 0 : Math.max(0, fullWidth - width);
          let left = Math.min(block.left, max);
          const head = view.state.selection.main.head;
          if (this.revealCaret && !wrapping && head >= block.from && head <= block.to) {
            const coords = view.coordsAtPos(head);
            const line = lines.find((item) => item.getBoundingClientRect().top <= (coords?.top ?? -1)
                && item.getBoundingClientRect().bottom > (coords?.top ?? -1));
            if (coords && line) {
              const rect = line.getBoundingClientRect();
              const x = coords.left - rect.left + block.left;
              if (x < left + 12) left = Math.max(0, x - 12);
              else if (x > left + width - 20) left = Math.min(max, x - width + 20);
            }
          }
          return [{ bar, lines, from, fullWidth, max, left, oldLeft: block.left }];
        });
        return {
          measurements,
          doc: view.state.doc,
          height: view.contentHeight,
          width: view.lineWrapping ? view.scrollDOM.clientWidth : view.scrollDOM.scrollWidth,
        };
      },
      write: ({ measurements, doc, height, width }, view) => {
        this.revealCaret = false;
        for (const layer of view.scrollDOM.querySelectorAll<HTMLElement>(":scope > .cm-cursorLayer, :scope > .cm-selectionLayer")) {
          layer.style.width = `${width}px`;
          layer.style.height = `${height}px`;
        }
        const effects: ReturnType<typeof scrollCodeBlock.of>[] = [];
        for (const { bar, lines, from, fullWidth, max, left, oldLeft } of measurements) {
          if (bar) {
            bar.setAttribute("aria-controls", lines.map((line) => {
              line.id ||= `md-code-scroll-line-${++nextCodeScrollbarId}`;
              return line.id;
            }).join(" "));
            bar.firstElementChild!.setAttribute("style", `width: ${fullWidth}px`);
            bar.classList.toggle("scrollable", max > 0);
            bar.tabIndex = max > 0 ? 0 : -1;
            bar.setAttribute("aria-valuemin", "0");
            bar.setAttribute("aria-valuemax", String(Math.round(max)));
            bar.setAttribute("aria-valuenow", String(Math.round(left)));
            bar.scrollLeft = left;
          }
          if (Math.abs(left - oldLeft) > 0.5) effects.push(scrollCodeBlock.of({ from, left }));
        }
        if (effects.length) queueMicrotask(() => {
          if (!this.destroyed && view.state.doc === doc) view.dispatch({ effects });
        });
      },
    });
  }
  destroy(): void { this.destroyed = true; }
}, {
  eventHandlers: {
    wheel(event, view) {
      if (view.dom.ownerDocument.documentElement.dataset.codeWordWrap === "true") return false;
      const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
      const line = (event.target as Element).closest<HTMLElement>(".md-code-line");
      if (!delta || !line) return false;
      const from = Number(line.dataset.codeBlock);
      const block = view.state.field(codeBlockLineDecorations).blocks.find((item) => item.from === from);
      const bar = view.contentDOM.querySelector<HTMLElement>(`.md-code-scrollbar[data-code-block="${from}"]`);
      const fullWidth = Math.max((block?.columns ?? 0) * view.defaultCharacterWidth,
        line.querySelector<HTMLElement>(".md-code-scroll-text")?.scrollWidth ?? 0) + codeLineInsets(line);
      const max = fullWidth - (bar?.clientWidth || line.getBoundingClientRect().width);
      if (!block || max <= 0) return false;
      view.dispatch({ effects: scrollCodeBlock.of({ from, left: Math.max(0, Math.min(max, block.left + delta)) }) });
      event.preventDefault();
      return true;
    },
  },
});

function moveToCodeLineBoundary(view: EditorView, forward: boolean, extend = false): boolean {
  if (view.dom.ownerDocument.documentElement.dataset.codeWordWrap === "true") return false;
  const { state } = view;
  const { decorations } = state.field(codeBlockLineDecorations);
  const lines = state.selection.ranges.map((range) => state.doc.lineAt(range.head));
  if (!lines.every((line) => {
    let code = false;
    decorations.between(line.from, line.from, () => { code = true; });
    return code;
  })) return false;
  // CodeMirror's wrapped-line Home/End uses the visible pane edge. Code lines
  // keep their full width, so move to their actual boundary even while scrolled.
  const ranges = state.selection.ranges.map((range, index) => {
    const line = lines[index]!;
    let head = forward ? line.to : line.from;
    if (!forward) {
      const indentEnd = line.from + /^\s*/.exec(line.text.slice(0, 100))![0].length;
      if (range.head !== indentEnd) head = indentEnd;
    }
    return extend ? EditorSelection.range(range.anchor, head) : EditorSelection.cursor(head);
  });
  view.dispatch({
    selection: EditorSelection.create(ranges, state.selection.mainIndex),
    scrollIntoView: true,
    userEvent: "select",
  });
  return true;
}

const codeCursorColumns = new WeakMap<EditorView, { doc: EditorState["doc"]; selection: EditorSelection; columns: number[] }>();

function moveWithinCodeLines(view: EditorView, forward: boolean, extend = false): boolean {
  if (view.dom.ownerDocument.documentElement.dataset.codeWordWrap === "true") return false;
  const { state } = view;
  const { decorations } = state.field(codeBlockLineDecorations);
  const lines = state.selection.ranges.map((range) => state.doc.lineAt(range.head));
  if (!lines.every((line) => {
    let code = false;
    decorations.between(line.from, line.from, () => { code = true; });
    return code;
  })) return false;
  const previous = codeCursorColumns.get(view);
  const columns = previous?.doc === state.doc && previous.selection.eq(state.selection)
    ? previous.columns : state.selection.ranges.map((range, index) =>
      countColumn(lines[index]!.text.slice(0, range.head - lines[index]!.from), state.tabSize));
  // Only prose wraps. CodeMirror's wrapped-line measurements can still assume
  // code is wrapped, so use physical code lines and retain the desired column.
  const ranges = state.selection.ranges.map((range, index) => {
    const number = lines[index]!.number + (forward ? 1 : -1);
    const target = number < 1 ? 0 : number > state.doc.lines ? state.doc.length
      : state.doc.line(number).from + findColumn(state.doc.line(number).text, columns[index]!, state.tabSize);
    return extend ? EditorSelection.range(range.anchor, target) : EditorSelection.cursor(target);
  });
  const selection = EditorSelection.create(ranges, state.selection.mainIndex);
  codeCursorColumns.set(view, { doc: state.doc, selection, columns });
  view.dispatch({ selection, scrollIntoView: true, userEvent: "select" });
  return true;
}

const livePreviewDecorations = StateField.define<DecorationSet>({
  create: buildLivePreviewDecorations,
  update(decorations, transaction) {
    return transaction.docChanged || transaction.selection
      || transaction.effects.some((effect) => effect.is(refreshLivePreview))
      ? buildLivePreviewDecorations(transaction.state)
      : decorations;
  },
  provide: (field) => EditorView.decorations.from(field),
});

const livePreviewViewportRefresh = ViewPlugin.fromClass(class {
  private pending = false;
  private destroyed = false;

  update(update: { viewportChanged: boolean; docChanged: boolean; selectionSet: boolean; view: EditorView }): void {
    if (!update.viewportChanged || update.docChanged || update.selectionSet || this.pending) return;
    this.pending = true;
    queueMicrotask(() => {
      this.pending = false;
      if (!this.destroyed) update.view.dispatch({ effects: refreshLivePreview.of() });
    });
  }

  destroy(): void {
    this.destroyed = true;
  }
});

function livePreviewListTextStart(state: EditorState, position: number): number | null {
  const line = state.doc.lineAt(position);
  let textStart: number | null = null;
  syntaxTree(state).iterate({
    from: line.from,
    to: line.to,
    enter(node) {
      if (node.name !== "ListMark" || node.from < line.from || node.to > line.to) return;
      const spacing = /^[\t ]+(?:\[[ xX]\](?:[\t ]+|$))?/
        .exec(state.doc.sliceString(node.to, line.to));
      textStart = node.to + (spacing?.[0].length ?? 0);
    },
  });
  return textStart;
}

function moveToLivePreviewLineStart(view: EditorView, extend = false): boolean {
  const { state } = view;
  const textStarts = state.selection.ranges.map((range) => livePreviewListTextStart(state, range.head));
  if (textStarts.every((start) => start === null)) return false;

  const ranges = state.selection.ranges.map((range, index) => {
    const line = view.lineBlockAt(range.head);
    let moved = view.moveToLineBoundary(range, false);
    if (moved.head === range.head && moved.head !== line.from) {
      moved = view.moveToLineBoundary(range, false, false);
    }
    let head = moved.head;
    const textStart = textStarts[index];
    if (textStart !== null && textStart !== undefined) {
      // Keep wrapped-line navigation, but stop line-start shortcuts after the list prefix.
      head = Math.max(head, textStart);
    } else if (head === line.from && line.length) {
      const indentation = /^\s*/.exec(
        state.doc.sliceString(line.from, Math.min(line.from + 100, line.to)),
      )![0].length;
      if (indentation && range.head !== line.from + indentation) head += indentation;
    }
    return extend ? EditorSelection.range(range.anchor, head) : EditorSelection.cursor(head, 1);
  });
  const selection = EditorSelection.create(ranges, state.selection.mainIndex);
  if (!selection.eq(state.selection, true)) {
    view.dispatch({ selection, scrollIntoView: true, userEvent: "select" });
  }
  // Consume repeated presses at the text start so the default keymap cannot reveal the marker.
  return true;
}

const mermaidEditorWidth = ViewPlugin.fromClass(class {
  private observer: ResizeObserver | null = null;
  private readonly onResize = () => this.measure();

  constructor(private readonly view: EditorView) {
    this.measure();
    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(this.onResize);
      this.observer.observe(view.scrollDOM);
    }
    window.addEventListener("resize", this.onResize);
  }

  private measure(): void {
    const viewportWidth = this.view.scrollDOM.clientWidth;
    if (!viewportWidth) return;
    const style = getComputedStyle(this.view.contentDOM);
    const padding = (parseFloat(style.paddingLeft) || 0)
      + (parseFloat(style.paddingRight) || 0);
    const available = Math.max(1, Math.floor(viewportWidth - padding));
    this.view.dom.style.setProperty("--md-mermaid-editor-width", `${available}px`);
  }

  destroy(): void {
    this.observer?.disconnect();
    window.removeEventListener("resize", this.onResize);
    this.view.dom.style.removeProperty("--md-mermaid-editor-width");
  }
});

const livePreviewExtension = [
  EditorView.editorAttributes.of({ class: "markdown-live-preview" }),
  Prec.high(keymap.of([
    { key: "Home", run: moveToLivePreviewLineStart, shift: (view) => moveToLivePreviewLineStart(view, true) },
    { mac: "Cmd-ArrowLeft", run: moveToLivePreviewLineStart, shift: (view) => moveToLivePreviewLineStart(view, true) },
  ])),
  livePreviewDecorations,
  livePreviewViewportRefresh,
  mermaidEditorWidth,
];

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

// Spell checking underlines the words the checker doesn't know, in place of the browser's
// own underlines, so they, the suggestions on right-click, and the words added to the
// dictionary all agree. The checker loads after the editor, so until then there are none.
let spellChecker: SpellChecker | null = null;
const spellingViews = new Set<EditorView>();
const refreshSpelling = StateEffect.define<null>();

/** Gives every editor the spell checker once it has loaded, or again once a word is added. */
export function setSpellChecker(checker: SpellChecker | null): void {
  spellChecker = checker;
  for (const view of spellingViews) view.dispatch({ effects: refreshSpelling.of(null) });
}

/** Code, the addresses of links and images, and HTML hold no words to check. */
const SPELLING_EXCLUDED_NODE_NAMES = new Set([
  ...CODE_NODE_NAMES,
  "URL",
  "Autolink",
  "LinkReference",
  "HTMLTag",
  "HTMLBlock",
  "CommentBlock",
]);

/** The words to check from `from` to `to`: outside code, addresses, @mentions, and [[links]]. */
function checkableWords(state: EditorState, from: number, to: number): SpellingWord[] {
  const text = state.doc.sliceString(from, to);
  const skipped = nonWordRanges(text, from);
  syntaxTree(state).iterate({
    from,
    to,
    enter(node) {
      if (!SPELLING_EXCLUDED_NODE_NAMES.has(node.name)) return true;
      skipped.push({ from: node.from, to: node.to });
      return false;
    },
  });
  return spellingWords(text, from)
    .filter((word) => !skipped.some((range) => word.from < range.to && word.to > range.from));
}

const misspelledMark = Decoration.mark({ class: "md-misspelled" });

/**
 * Underlines unknown words in the lines drawn. The word being typed waits until the cursor
 * leaves it, as it is bound to be unfinished.
 */
const spellingUnderlines = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    typing: number | null = null;

    constructor(readonly view: EditorView) {
      spellingViews.add(view);
      this.decorations = this.underline();
    }

    update(update: ViewUpdate): void {
      const typed = update.docChanged
        && update.transactions.some((transaction) => transaction.isUserEvent("input") || transaction.isUserEvent("delete"));
      const typing = typed ? update.state.selection.main.head : update.selectionSet ? null : this.typing;
      const refreshed = update.transactions.some((transaction) =>
        transaction.effects.some((effect) => effect.is(refreshSpelling)));
      if (!refreshed && !update.docChanged && !update.viewportChanged && typing === this.typing) return;
      this.typing = typing;
      this.decorations = this.underline();
    }

    underline(): DecorationSet {
      const checker = spellChecker;
      if (!checker) return Decoration.none;
      const { state, viewport } = this.view;
      const from = state.doc.lineAt(viewport.from).from;
      const to = state.doc.lineAt(viewport.to).to;
      const builder = new RangeSetBuilder<Decoration>();
      for (const word of checkableWords(state, from, to)) {
        if (this.typing !== null && word.from <= this.typing && this.typing <= word.to) continue;
        if (!checker.correct(word.word)) builder.add(word.from, word.to, misspelledMark);
      }
      return builder.finish();
    }

    destroy(): void {
      spellingViews.delete(this.view);
    }
  },
  { decorations: (plugin) => plugin.decorations },
);

/** Ours on: the browser's spell checking off. Ours off: the browser's back on. */
const spellCheckExtension = (on: boolean) => on
  ? [spellingUnderlines, EditorView.contentAttributes.of({ spellcheck: "false" })]
  : EditorView.contentAttributes.of({ spellcheck: "true" });

/**
 * What typing each of these over a selection puts around it, in place of replacing it: the
 * Markdown marks (code, emphasis, subscript, superscript) and brackets.
 */
const SELECTION_WRAPPERS: Readonly<Record<string, string>> = {
  "`": "`", "*": "*", "_": "_", "~": "~", "^": "^", "(": ")", "[": "]", "{": "}",
};

/**
 * Typing a Markdown mark or an opening bracket over selected text wraps it, and the text
 * stays selected inside, so typing another adds a second layer: * twice makes **bold**,
 * ~ twice ~~strikethrough~~, and [ twice a [[wiki link]].
 */
const wrapSelectionOnType = EditorView.inputHandler.of((view, _from, _to, text) => {
  const close = SELECTION_WRAPPERS[text];
  const { ranges } = view.state.selection;
  if (!close || view.composing || view.state.readOnly || ranges.some((range) => range.empty)) return false;
  view.dispatch(view.state.changeByRange((range) => ({
    changes: [{ from: range.from, insert: text }, { from: range.to, insert: close }],
    range: EditorSelection.range(range.anchor + 1, range.head + 1),
  })), { scrollIntoView: true, userEvent: "input.type" });
  return true;
});

/** Scrolls a read-only note, where the caret is hidden and arrows would move it unseen. */
function scrollReadOnly(view: EditorView, by: "line" | "page" | "end", forward: boolean): boolean {
  const scroller = view.scrollDOM;
  const distance = by === "line" ? view.defaultLineHeight * 2
    : by === "page" ? Math.max(scroller.clientHeight - view.defaultLineHeight * 2, view.defaultLineHeight)
      : scroller.scrollHeight;
  scroller.scrollBy({ top: forward ? distance : -distance });
  return true;
}

/**
 * Read-only, as Guide.md and a pane in Preview mode are: nothing typed, pasted, dropped, or
 * clicked changes the note, but it can still take focus, so it scrolls from the keyboard
 * and its text can be selected, searched, and copied. Changes the editor is given, with
 * `filter: false`, still arrive, so the note keeps up with edits made elsewhere.
 */
const readOnlyExtension = [
  EditorState.readOnly.of(true),
  EditorView.editable.of(false),
  // An edit is dropped whole, selection and all: a change filter would map the selection
  // back through the edit it took out, which can leave it running backwards.
  EditorState.transactionFilter.of((transaction) => (transaction.docChanged ? [] : transaction)),
  EditorView.editorAttributes.of({ class: "markdown-read-only" }),
  EditorView.contentAttributes.of({ tabindex: "0" }),
  Prec.highest(keymap.of([
    { key: "ArrowDown", run: (view) => scrollReadOnly(view, "line", true) },
    { key: "ArrowUp", run: (view) => scrollReadOnly(view, "line", false) },
    { key: "PageDown", run: (view) => scrollReadOnly(view, "page", true) },
    { key: "PageUp", run: (view) => scrollReadOnly(view, "page", false) },
    { key: "Space", run: (view) => scrollReadOnly(view, "page", true), shift: (view) => scrollReadOnly(view, "page", false) },
    { key: "End", run: (view) => scrollReadOnly(view, "end", true) },
    { key: "Home", run: (view) => scrollReadOnly(view, "end", false) },
  ])),
];

export class MarkdownEditor {
  private readonly lineNumberCompartment = new Compartment();
  private readonly readOnlyCompartment = new Compartment();
  private readonly historyCompartment = new Compartment();
  private readonly modeCompartment = new Compartment();
  private readonly wrapCompartment = new Compartment();
  private readonly spellCompartment = new Compartment();
  private spellCheck: boolean;
  private readonly view: EditorView;
  private readonly slashMenu: HTMLDivElement;
  private readonly wikiLinkMenu: HTMLDivElement;
  private readonly mentionMenu: HTMLDivElement;
  private readonly getWikiLinkSuggestions: (query: string) => readonly string[];
  private readonly getMentionSuggestions: (query: string) => readonly string[];
  private readonly onMentions?: MarkdownEditorOptions["onMentions"];
  private readonly getTodayText: () => string;
  private readonly onAskAi?: MarkdownEditorOptions["onAskAi"];
  private readonly resolveImage: (source: string) => string | null;
  private readonly getImageBorder: () => BorderStyle;
  private readonly getMermaidSettings: () => MermaidSettings;
  private readonly onImageFile?: MarkdownEditorOptions["onImageFile"];
  private readonly onImageError?: MarkdownEditorOptions["onImageError"];
  private lineNumbersVisible = false;
  private applyingExternalValue = false;
  private formatting = false;
  private sourceTableFormatTarget: number | null = null;
  private liveTableFrom: number | null = null;
  private mode: EditorMode;
  private slashCommands: readonly SlashCommand[] = [];
  private slashSelection = 0;
  private slashQuery = "";
  private wikiLinkSuggestions: readonly string[] = [];
  private wikiLinkSelection = 0;
  private wikiLinkQuery = "";
  private mentionSuggestions: readonly string[] = [];
  private mentionSelection = 0;
  private mentionQuery = "";
  private lineWrapping = true;
  private pasteAsPlainText = false;

  constructor(parent: HTMLElement, options: MarkdownEditorOptions) {
    this.mode = options.initialMode ?? "source";
    this.spellCheck = options.spellCheck ?? false;
    this.slashMenu = document.createElement("div");
    this.slashMenu.className = "editor-command-menu";
    this.slashMenu.setAttribute("role", "listbox");
    this.slashMenu.setAttribute("aria-label", "Markdown slash commands");
    this.slashMenu.hidden = true;
    this.wikiLinkMenu = document.createElement("div");
    this.wikiLinkMenu.className = "editor-command-menu wiki-link-menu";
    this.wikiLinkMenu.setAttribute("role", "listbox");
    this.wikiLinkMenu.setAttribute("aria-label", "Wiki link suggestions");
    this.wikiLinkMenu.hidden = true;
    this.mentionMenu = document.createElement("div");
    this.mentionMenu.className = "editor-command-menu mention-menu";
    this.mentionMenu.setAttribute("role", "listbox");
    this.mentionMenu.setAttribute("aria-label", "People to mention");
    this.mentionMenu.hidden = true;
    this.getWikiLinkSuggestions = options.getWikiLinkSuggestions ?? (() => []);
    this.getMentionSuggestions = options.getMentionSuggestions ?? (() => []);
    this.onMentions = options.onMentions;
    this.getTodayText = options.getTodayText ?? (() => formatToday(new Date()));
    this.onAskAi = options.onAskAi;
    this.resolveImage = options.resolveImage ?? defaultImageSource;
    this.getImageBorder = options.getImageBorder ?? (() => "none");
    this.getMermaidSettings = options.getMermaidSettings ?? (() => DEFAULT_MERMAID_SETTINGS);
    this.onImageFile = options.onImageFile;
    this.onImageError = options.onImageError;
    this.view = new EditorView({
      parent,
      state: EditorState.create({
        extensions: [
          highlightSpecialChars(),
          this.historyCompartment.of(history()),
          drawSelection(),
          dropCursor(),
          bracketMatching(),
          EditorState.allowMultipleSelections.of(true),
          this.readOnlyCompartment.of(options.readOnly ? readOnlyExtension : []),
          EditorState.tabSize.of(2),
          indentUnit.of("  "),
          this.wrapCompartment.of(EditorView.lineWrapping),
          codeBlockLineDecorations,
          codeBlockScrolling,
          fittedTableColumns,
          EditorView.contentAttributes.of({
            "aria-label": "Markdown note editor",
            autocapitalize: "sentences",
          }),
          this.spellCompartment.of(spellCheckExtension(this.spellCheck)),
          markdownLanguage,
          Prec.high(keymap.of([
            { key: "Home", run: (view) => moveToCodeLineBoundary(view, false), shift: (view) => moveToCodeLineBoundary(view, false, true) },
            { key: "End", run: (view) => moveToCodeLineBoundary(view, true), shift: (view) => moveToCodeLineBoundary(view, true, true) },
            { key: "Mod-b", run: () => this.formatSelection("bold") },
            { key: "Mod-i", run: () => this.formatSelection("italic") },
            { key: "Mod-Shift-k", run: () => this.formatSelection("link") },
            { key: "ArrowDown", run: () => this.moveMentionSelection(1) },
            { key: "ArrowUp", run: () => this.moveMentionSelection(-1) },
            { key: "Enter", run: () => this.applySelectedMention() },
            { key: "Tab", run: () => this.applySelectedMention() },
            { key: "Escape", run: () => this.dismissMentionMenu() },
            { key: "ArrowDown", run: () => this.moveWikiLinkSelection(1) },
            { key: "ArrowUp", run: () => this.moveWikiLinkSelection(-1) },
            { key: "Enter", run: () => this.applySelectedWikiLink() },
            { key: "Tab", run: () => this.applySelectedWikiLink() },
            { key: "Tab", run: indentOrderedList, shift: outdentOrderedList },
            { key: "Escape", run: () => this.dismissWikiLinkMenu() },
            { key: "ArrowDown", run: () => this.moveSlashSelection(1) },
            { key: "ArrowUp", run: () => this.moveSlashSelection(-1) },
            { key: "Enter", run: () => this.applySelectedSlashCommand() },
            { key: "Escape", run: () => this.dismissSlashMenu() },
            { key: "ArrowDown", run: (view) => moveWithinCodeLines(view, true), shift: (view) => moveWithinCodeLines(view, true, true) },
            { key: "ArrowUp", run: (view) => moveWithinCodeLines(view, false), shift: (view) => moveWithinCodeLines(view, false, true) },
            { key: "Enter", run: (view) => !view.state.readOnly && continueMarkdownMarkup(view, this.mode === "live") },
            { key: "Backspace", run: deleteMarkdownMarkupBackward },
          ])),
          syntaxHighlighting(markdownHighlightStyle),
          editorTheme,
          editorLinkDecorations,
          this.modeCompartment.of(this.mode === "live"
            ? [codeBlockMode.of(true), ...livePreviewExtension, imageResolverFacet.of(this.resolveImage),
              imageBorderFacet.of(this.getImageBorder),
              mermaidSettingsFacet.of(this.getMermaidSettings),
              tableFocusFacet.of((from) => { this.liveTableFrom = from; })] : sourceListLines),
          this.lineNumberCompartment.of([]),
          aiTargetExtension,
          // Typing / over a selection asks AI about it instead of replacing it.
          EditorView.inputHandler.of((view, _from, _to, text) => {
            const { ranges, main } = view.state.selection;
            if (text !== "/" || !this.onAskAi || main.empty || ranges.length > 1) return false;
            this.onAskAi("selection");
            return true;
          }),
          wrapSelectionOnType,
          keymap.of([indentWithTab, ...defaultKeymap, ...searchKeymap, ...historyKeymap]),
          EditorView.updateListener.of((update) => {
            if (update.docChanged && !this.applyingExternalValue) {
              options.onChange(update.state.doc.toString());
              this.reportMentions(update);
            }
            if (this.mode === "source" && !this.applyingExternalValue && !this.formatting) {
              let tables: ReturnType<typeof parseMarkdownTables> | null = null;
              if (update.docChanged) {
                const line = update.state.doc.lineAt(update.state.selection.main.head);
                if (line.text.includes("|")) {
                  tables = parseMarkdownTables(update.state.doc.toString());
                  const table = tables.find((candidate) =>
                    candidate.from <= update.state.selection.main.head
                    && update.state.selection.main.head <= candidate.to);
                  if (table) this.sourceTableFormatTarget = table.from;
                }
              }
              if (this.sourceTableFormatTarget !== null && (update.selectionSet || update.focusChanged)) {
                const head = update.state.selection.main.head;
                tables ??= parseMarkdownTables(update.state.doc.toString());
                const table = tables.find((candidate) => candidate.from === this.sourceTableFormatTarget);
                const insideTable = table && table.from <= head && head <= table.to;
                if (!insideTable || !update.view.hasFocus) {
                  const target = this.sourceTableFormatTarget;
                  this.sourceTableFormatTarget = null;
                  queueMicrotask(() => {
                    if (this.mode === "source") this.formatTableAt(target, false);
                  });
                }
              }
            }
            if (update.docChanged || update.selectionSet) {
              const head = update.state.selection.main.head;
              const line = update.state.doc.lineAt(head);
              options.onCursorChange(line.number, head - line.from + 1);
            }
            if (update.selectionSet && this.liveTableFrom !== null) {
              // Moving the caret out of a table's range means the user left it behind.
              const table = tableAtPosition(
                update.state.doc.toString(),
                update.state.selection.main.head,
              );
              if (table?.from !== this.liveTableFrom) this.liveTableFrom = null;
            }
            if (update.docChanged || update.selectionSet || update.viewportChanged || update.focusChanged) {
              this.updateSlashMenu();
              this.updateWikiLinkMenu();
              this.updateMentionMenu();
            }
          }),
          EditorView.domEventHandlers({
            keydown: (event) => {
              if (
                (event.metaKey || event.ctrlKey)
                && event.shiftKey
                && event.key.toLocaleLowerCase() === "v"
              ) {
                this.pasteAsPlainText = true;
              }
              return false;
            },
            keyup: (event) => {
              if (event.key.toLocaleLowerCase() === "v") this.pasteAsPlainText = false;
              return false;
            },
            paste: (event, view) => {
              if (view.state.readOnly || view.state.selection.ranges.length !== 1) return false;
              const selection = view.state.selection.main;
              const clipboard = event.clipboardData;
              const files = Array.from(clipboard?.files ?? []);
              const imageFiles = (files.length ? files : Array.from(clipboard?.items ?? [])
                .map((item) => item.getAsFile()).filter((file): file is File => !!file))
                .filter((file) => file.type.startsWith("image/") || imageExtension(file));
              if (!this.pasteAsPlainText && imageFiles.length && this.onImageFile) {
                event.preventDefault();
                this.insertImageFiles(imageFiles, selection.from, selection.to);
                return true;
              }
              const pastedText = event.clipboardData?.getData("text/plain");
              if (!pastedText) return false;

              const useLiteralPaste = this.pasteAsPlainText;
              this.pasteAsPlainText = false;
              if (!this.pasteMarkdown(pastedText, useLiteralPaste)) return false;
              event.preventDefault();
              return true;
            },
            dragover: (event, view) => {
              if (!this.onImageFile || view.state.readOnly
                || !Array.from(event.dataTransfer?.types ?? []).includes("Files"))
                return false;
              event.preventDefault();
              if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
              return true;
            },
            drop: (event, view) => {
              if (!this.onImageFile || view.state.readOnly) return false;
              const imageFiles = Array.from(event.dataTransfer?.files ?? [])
                .filter((file) => file.type.startsWith("image/") || imageExtension(file));
              if (!imageFiles.length) return false;
              event.preventDefault();
              const position = view.posAtCoords({ x: event.clientX, y: event.clientY })
                ?? view.state.selection.main.head;
              this.insertImageFiles(imageFiles, position, position);
              return true;
            },
            // ⌘-click (Ctrl-click off a Mac, where Ctrl-click is a right-click) follows a link.
            // It acts on the press: letting the press move the cursor would redraw the link's
            // line, showing its Markdown, and the click would never arrive.
            mousedown(event, view) {
              if (event.button !== 0) return false;
              const followKey = IS_MAC ? event.metaKey : event.ctrlKey;
              // A read-only note, like the Guide, has nothing to edit, so a plain click will do.
              if (!followKey && !view.state.readOnly) return false;
              const target = event.target instanceof Element
                ? event.target.closest<HTMLElement>("[data-editor-link]")
                : null;
              const text = target?.dataset.editorLink;
              if (!text) return false;
              event.preventDefault();
              options.onActivateLink(text);
              return true;
            },
          }),
        ],
      }),
    });

    this.slashMenu.addEventListener("pointerdown", (event) => {
      const button = event.target instanceof Element
        ? event.target.closest<HTMLButtonElement>("[data-command-index]")
        : null;
      if (!button) return;
      event.preventDefault();
      const index = Number(button.dataset.commandIndex);
      if (!Number.isInteger(index) || !this.slashCommands[index]) return;
      this.slashSelection = index;
      this.applySelectedSlashCommand();
    });
    this.wikiLinkMenu.addEventListener("pointerdown", (event) => {
      const button = event.target instanceof Element
        ? event.target.closest<HTMLButtonElement>("[data-wiki-link-index]")
        : null;
      if (!button) return;
      event.preventDefault();
      const index = Number(button.dataset.wikiLinkIndex);
      if (!Number.isInteger(index) || !this.wikiLinkSuggestions[index]) return;
      this.wikiLinkSelection = index;
      this.applySelectedWikiLink();
    });
    this.mentionMenu.addEventListener("pointerdown", (event) => {
      const button = event.target instanceof Element
        ? event.target.closest<HTMLButtonElement>("[data-mention-index]")
        : null;
      if (!button) return;
      event.preventDefault();
      const index = Number(button.dataset.mentionIndex);
      if (!Number.isInteger(index) || !this.mentionSuggestions[index]) return;
      this.mentionSelection = index;
      this.applySelectedMention();
    });
    parent.appendChild(this.slashMenu);
    parent.appendChild(this.wikiLinkMenu);
    parent.appendChild(this.mentionMenu);
  }

  getValue(): string {
    return this.view.state.doc.toString();
  }

  /** Forgets every undo step, so nothing typed earlier can be brought back. */
  clearHistory(): void {
    this.view.dispatch({ effects: this.historyCompartment.reconfigure([]) });
    this.view.dispatch({ effects: this.historyCompartment.reconfigure(history()) });
  }

  setValue(value: string): void {
    const current = this.getValue();
    const changed = findChangedRange(current, value);
    if (!changed) return;

    this.sourceTableFormatTarget = null;
    this.liveTableFrom = null;

    this.applyingExternalValue = true;
    try {
      this.view.dispatch({
        changes: {
          from: changed.start,
          to: changed.previousEnd,
          insert: value.slice(changed.start, changed.nextEnd),
        },
        annotations: Transaction.addToHistory.of(false),
        // The note changed elsewhere; a read-only editor shows that too.
        filter: false,
      });
    } finally {
      this.applyingExternalValue = false;
    }
  }

  setLineNumbers(visible: boolean): void {
    if (visible === this.lineNumbersVisible) return;
    this.lineNumbersVisible = visible;
    this.view.dispatch({
      effects: this.lineNumberCompartment.reconfigure(visible ? lineNumbers() : []),
    });
  }

  /** On, the app's spell checker underlines unknown words; off, the browser's checks them. */
  setSpellCheck(on: boolean): void {
    if (on === this.spellCheck) return;
    this.spellCheck = on;
    this.view.dispatch({ effects: this.spellCompartment.reconfigure(spellCheckExtension(on)) });
  }

  /**
   * The word at a point on screen, or at the cursor, if the spell checker reads it, and
   * whether it is misspelled; null with spell checking off or not yet loaded.
   */
  spellingAt(at?: { x: number; y: number }): (SpellingWord & { misspelled: boolean }) | null {
    const checker = spellChecker;
    if (!checker || !this.spellCheck) return null;
    const position = at ? this.view.posAtCoords(at) : this.view.state.selection.main.head;
    if (position === null) return null;
    const line = this.view.state.doc.lineAt(position);
    const word = checkableWords(this.view.state, line.from, line.to)
      .find((candidate) => candidate.from <= position && position <= candidate.to);
    return word ? { ...word, misspelled: !checker.correct(word.word) } : null;
  }

  /** Off, long lines run on past the edge and the editor scrolls sideways. */
  setLineWrapping(wrap: boolean): void {
    if (wrap === this.lineWrapping) return;
    this.lineWrapping = wrap;
    this.view.dispatch({
      effects: this.wrapCompartment.reconfigure(wrap ? EditorView.lineWrapping : []),
    });
  }

  setMode(mode: EditorMode): void {
    if (mode === this.mode) return;
    if (this.mode === "source" && this.sourceTableFormatTarget !== null) {
      const target = this.sourceTableFormatTarget;
      this.sourceTableFormatTarget = null;
      this.formatTableAt(target, false);
    }
    this.mode = mode;
    this.liveTableFrom = null;
    this.hideSlashMenu();
    this.hideWikiLinkMenu();
    this.hideMentionMenu();
    this.view.dispatch({
      effects: this.modeCompartment.reconfigure(mode === "live"
        ? [codeBlockMode.of(true), ...livePreviewExtension, imageResolverFacet.of(this.resolveImage),
          imageBorderFacet.of(this.getImageBorder),
          mermaidSettingsFacet.of(this.getMermaidSettings),
          tableFocusFacet.of((from) => { this.liveTableFrom = from; })] : sourceListLines),
    });
  }

  getMode(): EditorMode {
    return this.mode;
  }

  /** Read-only, the note shows as it is and nothing done in this editor changes it. */
  setReadOnly(readOnly: boolean): void {
    if (readOnly === this.view.state.readOnly) return;
    if (readOnly) {
      // A table typed into in Source is tidied as it would be on leaving it.
      if (this.mode === "source" && this.sourceTableFormatTarget !== null) {
        const target = this.sourceTableFormatTarget;
        this.sourceTableFormatTarget = null;
        this.formatTableAt(target, false);
      }
      // A table cell open in Live Preview saves what was typed into it as it closes.
      const focused = this.view.root.activeElement;
      if (focused instanceof HTMLElement && focused !== this.view.contentDOM && this.view.dom.contains(focused))
        focused.blur();
      this.liveTableFrom = null;
      this.hideSlashMenu();
      this.hideWikiLinkMenu();
      this.hideMentionMenu();
    }
    this.view.dispatch({
      effects: [this.readOnlyCompartment.reconfigure(readOnly ? readOnlyExtension : []), refreshLivePreview.of()],
    });
  }

  focus(): void {
    this.view.focus();
  }

  /** Puts the cursor at the start of a line, 1-based, and brings it into view. */
  goToLine(line: number): void {
    const { doc } = this.view.state;
    const target = doc.line(Math.min(Math.max(1, Math.round(line)), doc.lines));
    this.view.dispatch({
      selection: { anchor: target.from },
      scrollIntoView: true,
    });
  }

  /** Puts the cursor at the start of a line, 1-based, with the line at the top of the view. */
  goToLineAtTop(line: number): void {
    const { doc } = this.view.state;
    const target = doc.line(Math.min(Math.max(1, Math.round(line)), doc.lines));
    this.view.dispatch({ selection: { anchor: target.from } });
    // Scrolled as setPlace does, on the editor alone, so the pane keeps its title in view.
    // Lines not yet drawn have estimated heights, so once the scroll draws them it is
    // measured again and corrected, a few times at most.
    const align = (tries: number) => this.view.requestMeasure({
      read: () => {
        const scroller = this.view.scrollDOM;
        // Line heights count from the top of the text, which sits below the editor's padding.
        const start = this.view.documentTop - scroller.getBoundingClientRect().top + scroller.scrollTop;
        const top = Math.min(
          Math.max(0, start + this.view.lineBlockAt(target.from).top - 8),
          scroller.scrollHeight - scroller.clientHeight,
        );
        return { top, now: scroller.scrollTop };
      },
      write: ({ top, now }) => {
        if (Math.abs(top - now) < 1) return;
        this.view.scrollDOM.scrollTop = top;
        if (tries > 1) align(tries - 1);
      },
    });
    align(4);
    this.view.focus();
  }

  requestMeasure(): void {
    this.view.requestMeasure();
  }

  refreshPreview(): void {
    this.view.dispatch({ effects: refreshLivePreview.of() });
  }

  private insertImageFiles(files: File[], from: number, to: number): void {
    if (!this.onImageFile) return;
    const placeholders = files.map(() =>
      `![Uploading image](turbonotes-pending:${crypto.randomUUID()})`);
    const insert = placeholders.join("\n");
    this.view.dispatch({
      changes: { from, to, insert },
      selection: EditorSelection.cursor(from + insert.length),
      scrollIntoView: true,
      annotations: Transaction.userEvent.of("input.paste"),
    });
    files.forEach((file, index) => {
      const placeholder = placeholders[index]!;
      void this.onImageFile!(file, placeholder).then((markdown) => {
        if (markdown !== null) this.replaceImagePlaceholder(placeholder, markdown);
      }).catch((error: unknown) => {
        this.replaceImagePlaceholder(placeholder, "");
        this.onImageError?.(error instanceof Error ? error.message : "Image insertion failed.");
      });
    });
  }

  private replaceImagePlaceholder(placeholder: string, markdown: string): void {
    const from = this.view.state.doc.toString().indexOf(placeholder);
    if (from < 0) return;
    this.view.dispatch({
      changes: { from, to: from + placeholder.length, insert: markdown },
      annotations: Transaction.userEvent.of("input.paste"),
      // An image pasted before the note became read-only still lands where it was put.
      filter: false,
    });
  }

  hasFocus(): boolean {
    return this.view.hasFocus;
  }

  isReadOnly(): boolean {
    return this.view.state.readOnly;
  }

  /**
   * The paste ⌘V makes of text, where it differs from the text itself: a URL becomes a link,
   * and list items take the list they land in. False when it is just the text, or `literal`.
   */
  private pasteMarkdown(text: string, literal: boolean): boolean {
    const view = this.view;
    const selection = view.state.selection.main;
    if (view.state.selection.ranges.length !== 1) return false;
    if (!literal && !selectionRequiresLiteralUrlPaste(view.state)) {
      const selectedText = view.state.doc.sliceString(selection.from, selection.to);
      const markdownLink = markdownForUrlPaste(text, selectedText);
      if (markdownLink) {
        const labelEnd = markdownLink.lastIndexOf("](");
        view.dispatch({
          changes: {
            from: selection.from,
            to: selection.to,
            insert: markdownLink,
          },
          // The label is selected, so typing replaces it.
          selection: EditorSelection.range(selection.from + 1, selection.from + labelEnd),
          scrollIntoView: true,
          annotations: Transaction.userEvent.of("input.paste"),
        });
        return true;
      }
    }

    if (!selection.empty) return false;

    const line = view.state.doc.lineAt(selection.head);
    const lineBeforeCursor = view.state.doc.sliceString(line.from, selection.head);
    const normalizedText = normalizePastedListItem(lineBeforeCursor, text);
    if (normalizedText === text) return false;

    view.dispatch({
      changes: { from: selection.head, insert: normalizedText },
      selection: EditorSelection.cursor(selection.head + normalizedText.length),
      scrollIntoView: true,
      annotations: Transaction.userEvent.of("input.paste"),
    });
    return true;
  }

  /** Pastes text as ⌘V would, or as it is with `plain`, as ⌘⇧V would. */
  paste(text: string, plain = false): void {
    if (this.view.state.readOnly) return;
    if (!this.pasteMarkdown(text, plain))
      this.view.dispatch(this.view.state.replaceSelection(text), {
        scrollIntoView: true,
        userEvent: "input.paste",
      });
    this.view.focus();
  }

  /** Puts text in place of the selection, as typing it would, with the cursor after it. */
  insertText(text: string): void {
    if (this.view.state.readOnly) return;
    this.view.dispatch(this.view.state.replaceSelection(text), { scrollIntoView: true, userEvent: "input" });
    this.view.focus();
  }

  /** Puts a block in place of the selection on lines of its own, with the cursor at its end. */
  insertBlock(block: string): void {
    if (this.view.state.readOnly) return;
    const { from, to } = this.view.state.selection.main;
    const { prefix, suffix } = blockBreaks(this.view.state.doc.toString(), from, to);
    this.view.dispatch({
      changes: { from, to, insert: prefix + block + suffix },
      selection: EditorSelection.cursor(from + prefix.length + block.length),
      scrollIntoView: true,
      userEvent: "input",
    });
    this.view.focus();
  }

  /** Pastes images as ⌘V would, saving each and linking it where the selection was. */
  pasteImages(files: File[]): void {
    if (this.view.state.readOnly || !files.length) return;
    const { from, to } = this.view.state.selection.main;
    this.insertImageFiles(files, from, to);
    this.view.focus();
  }

  /** Selects from one offset to another and scrolls it into view. */
  select(anchor: number, head = anchor): void {
    const length = this.view.state.doc.length;
    const at = (position: number) => Math.max(0, Math.min(length, position));
    this.view.dispatch({ selection: { anchor: at(anchor), head: at(head) }, scrollIntoView: true });
    this.view.focus();
  }

  /** Makes edits as one step, undone together, as if they were typed. */
  change(changes: readonly { from: number; to: number; insert: string }[]): void {
    if (this.view.state.readOnly || !changes.length) return;
    this.view.dispatch({
      changes: [...changes],
      scrollIntoView: true,
      annotations: [Transaction.userEvent.of("input"), isolateHistory.of("full")],
    });
    this.view.focus();
  }

  /** The main selection, or null when nothing is selected. */
  getSelection(): { from: number; to: number; text: string } | null {
    const { from, to, empty } = this.view.state.selection.main;
    return empty ? null : { from, to, text: this.view.state.sliceDoc(from, to) };
  }

  getCursor(): number {
    return this.view.state.selection.main.head;
  }

  /** Where the cursor is on screen, for a menu opened from the keyboard; null when it is out of view. */
  cursorCoords(): { left: number; bottom: number } | null {
    return this.view.coordsAtPos(this.view.state.selection.main.head);
  }

  /** The selection and the line at the top of the view, to come back to after a reload. */
  getPlace(): EditorPlace {
    const { anchor, head } = this.view.state.selection.main;
    const { scrollTop } = this.view.scrollDOM;
    const line = this.view.lineBlockAtHeight(scrollTop);
    return { anchor, head, top: line.from, offset: scrollTop - line.top };
  }

  /** Puts back a place from `getPlace`, as far as the note still reaches. */
  setPlace(place: EditorPlace): void {
    const { length } = this.view.state.doc;
    const at = (position: number) => Math.max(0, Math.min(length, Math.round(position) || 0));
    this.view.dispatch({ selection: { anchor: at(place.anchor), head: at(place.head) } });
    const top = at(place.top);
    // As CodeMirror's own scroll snapshots do, this scrolls the editor alone: scrollIntoView
    // would scroll the panes around it too, pushing their title bars out of sight.
    this.view.requestMeasure({
      read: () => this.view.lineBlockAt(top).top + place.offset,
      write: (scrollTop) => { this.view.scrollDOM.scrollTop = scrollTop; },
    });
  }

  /** Marks the text an AI answer will replace, or go below; an empty range is where it goes in. */
  showAiTarget(from: number, to: number): void {
    this.view.dispatch({ effects: setAiTarget.of({ from, to, draft: null }) });
  }

  /** Shows the answer so far in place of the target; false once the target is gone. */
  streamAiDraft(text: string): boolean {
    const target = this.view.state.field(aiTargetField);
    if (!target) return false;
    this.view.dispatch({ effects: setAiTarget.of({ ...target, draft: text }) });
    return true;
  }

  /** Where the AI answer goes now, after edits elsewhere; null once the text itself was edited. */
  aiTarget(): { from: number; to: number } | null {
    const target = this.view.state.field(aiTargetField);
    return target && { from: target.from, to: target.to };
  }

  clearAiTarget(): void {
    if (this.view.state.field(aiTargetField)) this.view.dispatch({ effects: setAiTarget.of(null) });
  }

  /** Puts an answer in place of the target, or below it, as one undo step, and selects it. */
  applyAiAnswer(text: string, below = false): boolean {
    const target = this.view.state.field(aiTargetField);
    if (!target) return false;
    const place = below
      ? insertionBelow(this.view.state.doc.toString(), target.to)
      : { at: target.from, prefix: "", suffix: "" };
    const from = place.at + place.prefix.length;
    this.view.dispatch({
      changes: { from: place.at, to: below ? place.at : target.to, insert: place.prefix + text + place.suffix },
      selection: EditorSelection.range(from, from + text.length),
      effects: setAiTarget.of(null),
      annotations: [Transaction.userEvent.of("input.ai"), isolateHistory.of("full")],
      scrollIntoView: true,
    });
    this.view.focus();
    return true;
  }

  formatSelection(format: MarkdownFormat): boolean {
    const state = this.view.state;
    const selection = state.selection.main;

    if (isInlineMarkdownFormat(format)) {
      const selectedText = state.doc.sliceString(selection.from, selection.to);
      const result = formatInlineText(selectedText, format);
      this.view.dispatch({
        changes: { from: selection.from, to: selection.to, insert: result.text },
        selection: EditorSelection.range(
          selection.from + result.selectionStart,
          selection.from + result.selectionEnd,
        ),
        scrollIntoView: true,
        annotations: Transaction.userEvent.of("input"),
      });
      this.view.focus();
      return true;
    }

    const startLine = state.doc.lineAt(selection.from);
    const endProbe = selection.to > selection.from
      && selection.to === state.doc.lineAt(selection.to).from
      ? selection.to - 1
      : selection.to;
    const endLine = state.doc.lineAt(endProbe);
    const original = state.doc.sliceString(startLine.from, endLine.to);
    const formatted = formatMarkdownLines(original, format);
    this.view.dispatch({
      changes: { from: startLine.from, to: endLine.to, insert: formatted },
      selection: selection.empty
        ? EditorSelection.cursor(startLine.from + formatted.length)
        : EditorSelection.range(startLine.from, startLine.from + formatted.length),
      scrollIntoView: true,
      annotations: Transaction.userEvent.of("input"),
    });
    this.view.focus();
    return true;
  }

  insertTable(from?: number, to?: number): void {
    const state = this.view.state;
    const selection = state.selection.main;
    const insertFrom = from ?? selection.from;
    const insertTo = to ?? selection.to;
    const insertion = tableInsertion(state.doc.toString(), insertFrom, insertTo);
    this.view.dispatch({
      changes: { from: insertFrom, to: insertTo, insert: insertion.text },
      selection: this.mode === "live"
        ? EditorSelection.cursor(insertFrom + insertion.text.length)
        : EditorSelection.range(
          insertFrom + insertion.selectFrom,
          insertFrom + insertion.selectTo,
        ),
      scrollIntoView: true,
      annotations: Transaction.userEvent.of("input"),
    });
    if (this.mode === "live") {
      focusRenderedTableCell(this.view, insertFrom + insertion.selectFrom - 2, -1, 0);
    } else {
      this.view.focus();
    }
  }

  /** The Markdown of the table the cursor is on, or the live preview cell last opened. */
  tableMarkdownAtCursor(): string | null {
    const state = this.view.state;
    const source = state.doc.toString();
    const table = tableAtPosition(source, state.selection.main.head)
      ?? (this.liveTableFrom === null ? null : tableAtPosition(source, this.liveTableFrom));
    return table ? source.slice(table.from, table.to) : null;
  }

  formatTables(scope: "selection" | "document", focus = true): boolean {
    const state = this.view.state;
    const selection = scope === "selection"
      ? { from: state.selection.main.from, to: state.selection.main.to }
      : undefined;
    const changes = tableFormatChanges(state.doc.toString(), selection);
    return this.applyFormatChanges(changes, focus);
  }

  private formatTableAt(position: number, focus: boolean): boolean {
    const changes = tableFormatChanges(this.view.state.doc.toString(), {
      from: position,
      to: position,
    });
    return this.applyFormatChanges(changes, focus);
  }

  /** Clean up the whole note; null when it cannot be formatted without losing text. */
  formatDocument(): boolean | null {
    const changes = markdownFormatChanges(this.view.state.doc.toString());
    return changes && this.applyFormatChanges(changes, true);
  }

  private applyFormatChanges(
    changes: ReturnType<typeof tableFormatChanges>,
    focus: boolean,
  ): boolean {
    if (changes.length === 0) return false;
    this.formatting = true;
    try {
      this.view.dispatch({
        changes,
        annotations: Transaction.userEvent.of("input"),
      });
    } finally {
      this.formatting = false;
    }
    if (focus) this.view.focus();
    return true;
  }

  private updateWikiLinkMenu(): void {
    const selection = this.view.state.selection.main;
    if (!this.view.hasFocus || !selection.empty || this.view.state.readOnly) {
      this.hideWikiLinkMenu();
      return;
    }

    const line = this.view.state.doc.lineAt(selection.head);
    const context = getWikiLinkContext(
      this.view.state.doc.sliceString(line.from, selection.head),
    );
    if (!context) {
      this.hideWikiLinkMenu();
      return;
    }

    const suggestions = this.getWikiLinkSuggestions(context.query);
    if (suggestions.length === 0) {
      this.hideWikiLinkMenu();
      return;
    }

    if (context.query !== this.wikiLinkQuery) this.wikiLinkSelection = 0;
    this.wikiLinkQuery = context.query;
    this.wikiLinkSuggestions = suggestions;
    this.wikiLinkSelection = Math.min(this.wikiLinkSelection, suggestions.length - 1);
    this.renderWikiLinkMenu();
    this.wikiLinkMenu.hidden = false;
    this.positionCommandMenu(this.wikiLinkMenu, selection.head);
  }

  private renderWikiLinkMenu(): void {
    const title = document.createElement("div");
    title.className = "editor-command-title";
    title.textContent = this.wikiLinkQuery
      ? `LINK NOTE [[${this.wikiLinkQuery}`
      : "LINK NOTE [[";

    const list = document.createElement("div");
    list.className = "editor-command-list";
    this.wikiLinkSuggestions.forEach((suggestion, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.wikiLinkIndex = String(index);
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(index === this.wikiLinkSelection));
      button.className = "editor-command-row";

      const key = document.createElement("span");
      key.className = "editor-command-key";
      key.textContent = "[[";
      const label = document.createElement("span");
      label.className = "editor-command-label";
      label.textContent = suggestion;
      const detail = document.createElement("span");
      detail.className = "editor-command-detail";
      detail.textContent = "NOTE";
      button.appendChild(key);
      button.appendChild(label);
      button.appendChild(detail);
      list.appendChild(button);
    });

    const footer = document.createElement("div");
    footer.className = "editor-command-footer";
    footer.textContent = "↑↓ MOVE   ENTER/TAB LINK   ESC CLOSE";
    this.wikiLinkMenu.replaceChildren(title, list, footer);
  }

  private moveWikiLinkSelection(delta: number): boolean {
    if (this.wikiLinkMenu.hidden || this.wikiLinkSuggestions.length === 0) return false;
    this.wikiLinkSelection = (
      this.wikiLinkSelection + delta + this.wikiLinkSuggestions.length
    ) % this.wikiLinkSuggestions.length;
    this.renderWikiLinkMenu();
    this.wikiLinkMenu
      .querySelector<HTMLElement>(`[data-wiki-link-index="${this.wikiLinkSelection}"]`)
      ?.scrollIntoView({ block: "nearest" });
    return true;
  }

  private applySelectedWikiLink(): boolean {
    if (this.wikiLinkMenu.hidden) return false;
    const suggestion = this.wikiLinkSuggestions[this.wikiLinkSelection];
    if (!suggestion) return false;

    const selection = this.view.state.selection.main;
    const line = this.view.state.doc.lineAt(selection.head);
    const context = getWikiLinkContext(
      this.view.state.doc.sliceString(line.from, selection.head),
    );
    if (!context) return false;

    const from = line.from + context.openOffset;
    const link = `[[${suggestion}]]`;
    this.view.dispatch({
      changes: { from, to: selection.head, insert: link },
      selection: EditorSelection.cursor(from + link.length),
      scrollIntoView: true,
      annotations: Transaction.userEvent.of("input.complete"),
    });
    this.hideWikiLinkMenu();
    this.view.focus();
    return true;
  }

  private dismissWikiLinkMenu(): boolean {
    if (this.wikiLinkMenu.hidden) return false;
    this.hideWikiLinkMenu();
    return true;
  }

  private hideWikiLinkMenu(): void {
    this.wikiLinkMenu.hidden = true;
    this.wikiLinkSuggestions = [];
    this.wikiLinkQuery = "";
  }

  /** Tells the app about each @mention on the lines a change touched, once it is typed out. */
  private reportMentions(update: ViewUpdate): void {
    if (!this.onMentions) return;
    const state = update.state;
    const head = state.selection.main.head;
    const lines = new Set<number>();
    update.changes.iterChangedRanges((_fromA, _toA, fromB, toB) => {
      const last = state.doc.lineAt(toB).number;
      // A paste of a whole document is read up to a bound, so typing never gets slow.
      for (let number = state.doc.lineAt(fromB).number; number <= last && lines.size < 500; number++)
        lines.add(number);
    });
    const names: string[] = [];
    for (const number of lines) {
      const line = state.doc.line(number);
      for (const mention of mentionRanges(line.text)) {
        const from = line.from + mention.from;
        // The name the cursor is on may still be half typed: "@ana." on the way to "@ana.lopez".
        const typing = mention.to + /^[.-]*/.exec(line.text.slice(mention.to))![0].length;
        if (from <= head && head <= line.from + typing) continue;
        if (positionHasSyntaxAncestor(state, from, 1, MENTION_EXCLUDED_NODE_NAMES)) continue;
        names.push(mention.name);
      }
    }
    if (names.length) this.onMentions(names);
  }

  private updateMentionMenu(): void {
    const selection = this.view.state.selection.main;
    if (!this.view.hasFocus || !selection.empty || !this.wikiLinkMenu.hidden || this.view.state.readOnly) {
      this.hideMentionMenu();
      return;
    }

    const line = this.view.state.doc.lineAt(selection.head);
    const context = getMentionContext(
      this.view.state.doc.sliceString(line.from, selection.head),
    );
    if (
      !context
      || positionHasSyntaxAncestor(
        this.view.state, line.from + context.openOffset, 1, MENTION_EXCLUDED_NODE_NAMES,
      )
    ) {
      this.hideMentionMenu();
      return;
    }

    const suggestions = this.getMentionSuggestions(context.query);
    if (suggestions.length === 0) {
      this.hideMentionMenu();
      return;
    }

    if (context.query !== this.mentionQuery) this.mentionSelection = 0;
    this.mentionQuery = context.query;
    this.mentionSuggestions = suggestions;
    this.mentionSelection = Math.min(this.mentionSelection, suggestions.length - 1);
    this.renderMentionMenu();
    this.mentionMenu.hidden = false;
    this.positionCommandMenu(this.mentionMenu, selection.head);
  }

  private renderMentionMenu(): void {
    const title = document.createElement("div");
    title.className = "editor-command-title";
    title.textContent = `MENTION @${this.mentionQuery}`;

    const list = document.createElement("div");
    list.className = "editor-command-list";
    this.mentionSuggestions.forEach((name, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.mentionIndex = String(index);
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(index === this.mentionSelection));
      button.className = "editor-command-row";

      const key = document.createElement("span");
      key.className = "editor-command-key";
      key.textContent = "@";
      const label = document.createElement("span");
      label.className = "editor-command-label";
      label.textContent = name;
      const detail = document.createElement("span");
      detail.className = "editor-command-detail";
      detail.textContent = "PERSON";
      button.append(key, label, detail);
      list.appendChild(button);
    });

    const footer = document.createElement("div");
    footer.className = "editor-command-footer";
    footer.textContent = "↑↓ MOVE   ENTER/TAB MENTION   ESC CLOSE";
    this.mentionMenu.replaceChildren(title, list, footer);
  }

  private moveMentionSelection(delta: number): boolean {
    if (this.mentionMenu.hidden || this.mentionSuggestions.length === 0) return false;
    this.mentionSelection = (
      this.mentionSelection + delta + this.mentionSuggestions.length
    ) % this.mentionSuggestions.length;
    this.renderMentionMenu();
    this.mentionMenu
      .querySelector<HTMLElement>(`[data-mention-index="${this.mentionSelection}"]`)
      ?.scrollIntoView({ block: "nearest" });
    return true;
  }

  private applySelectedMention(): boolean {
    if (this.mentionMenu.hidden) return false;
    const name = this.mentionSuggestions[this.mentionSelection];
    if (!name) return false;

    const selection = this.view.state.selection.main;
    const line = this.view.state.doc.lineAt(selection.head);
    const context = getMentionContext(
      this.view.state.doc.sliceString(line.from, selection.head),
    );
    if (!context) return false;

    const from = line.from + context.openOffset;
    // A space ends the mention, unless one or some punctuation already follows.
    const next = this.view.state.doc.sliceString(selection.head, selection.head + 1);
    const mention = `@${name}${/^[\p{L}\p{N}_]?$/u.test(next) ? " " : ""}`;
    this.view.dispatch({
      changes: { from, to: selection.head, insert: mention },
      selection: EditorSelection.cursor(from + mention.length),
      scrollIntoView: true,
      annotations: Transaction.userEvent.of("input.complete"),
    });
    this.hideMentionMenu();
    this.onMentions?.([name]);
    this.view.focus();
    return true;
  }

  private dismissMentionMenu(): boolean {
    if (this.mentionMenu.hidden) return false;
    this.hideMentionMenu();
    return true;
  }

  private hideMentionMenu(): void {
    this.mentionMenu.hidden = true;
    this.mentionSuggestions = [];
    this.mentionQuery = "";
  }

  private positionCommandMenu(menu: HTMLElement, position: number): void {
    const coordinates = this.view.coordsAtPos(position);
    const parentRect = this.view.dom.parentElement?.getBoundingClientRect();
    if (!coordinates || !parentRect) return;
    const left = Math.max(4, Math.min(coordinates.left - parentRect.left, parentRect.width - 332));
    let top = coordinates.bottom - parentRect.top + 3;
    if (top + menu.offsetHeight > parentRect.height - 4) {
      top = Math.max(4, coordinates.top - parentRect.top - menu.offsetHeight - 3);
    }
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }

  private updateSlashMenu(): void {
    const selection = this.view.state.selection.main;
    if (!this.view.hasFocus || !selection.empty || this.view.state.readOnly) {
      this.hideSlashMenu();
      return;
    }

    const found = this.slashCommandsAt(selection.head);
    if (!found) {
      this.hideSlashMenu();
      return;
    }

    const { context, commands } = found;
    if (commands.length === 0) {
      this.hideSlashMenu();
      return;
    }

    if (context.query !== this.slashQuery) this.slashSelection = 0;
    this.slashQuery = context.query;
    this.slashCommands = commands;
    this.slashSelection = Math.min(this.slashSelection, commands.length - 1);
    this.renderSlashMenu();
    this.slashMenu.hidden = false;

    const coordinates = this.view.coordsAtPos(selection.head);
    if (!coordinates) {
      this.hideSlashMenu();
      return;
    }
    const parentRect = this.view.dom.parentElement?.getBoundingClientRect();
    if (!parentRect) return;
    const left = Math.max(4, Math.min(coordinates.left - parentRect.left, parentRect.width - 332));
    let top = coordinates.bottom - parentRect.top + 3;
    if (top + this.slashMenu.offsetHeight > parentRect.height - 4) {
      top = Math.max(4, coordinates.top - parentRect.top - this.slashMenu.offsetHeight - 3);
    }
    this.slashMenu.style.left = `${left}px`;
    this.slashMenu.style.top = `${top}px`;
  }

  /**
   * The slash command typed before `head` and the commands it matches: any of them at the
   * start of a line, or Ask AI alone further along, except in code, where /a is a path.
   */
  private slashCommandsAt(head: number): { context: SlashContext; commands: readonly SlashCommand[] } | null {
    const line = this.view.state.doc.lineAt(head);
    const before = this.view.state.doc.sliceString(line.from, head);
    const context = getSlashContext(before);
    if (context) return { context, commands: filterSlashCommands(context.query) };
    const ai = this.onAskAi ? getAiSlashContext(before) : null;
    if (!ai) return null;
    for (let node = syntaxTree(this.view.state).resolveInner(head, -1); ; node = node.parent) {
      if (/Code/.test(node.name)) return null;
      if (!node.parent) break;
    }
    return { context: ai, commands: SLASH_COMMANDS.filter((command) => command.id === "ai") };
  }

  private renderSlashMenu(): void {
    const title = document.createElement("div");
    title.className = "editor-command-title";
    title.textContent = this.slashQuery ? `COMMAND /${this.slashQuery}` : "SLASH COMMAND";

    const list = document.createElement("div");
    list.className = "editor-command-list";
    this.slashCommands.forEach((command, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.commandIndex = String(index);
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(index === this.slashSelection));
      button.className = "editor-command-row";

      const key = document.createElement("span");
      key.className = "editor-command-key";
      key.textContent = command.key;
      const label = document.createElement("span");
      label.className = "editor-command-label";
      label.textContent = command.label;
      const detail = document.createElement("span");
      detail.className = "editor-command-detail";
      detail.textContent = command.id === "today" ? this.getTodayText() : command.detail;
      button.appendChild(key);
      button.appendChild(label);
      button.appendChild(detail);
      list.appendChild(button);
    });

    const footer = document.createElement("div");
    footer.className = "editor-command-footer";
    footer.textContent = "↑↓ MOVE   ENTER INSERT   ESC CLOSE";
    this.slashMenu.replaceChildren(title, list, footer);
  }

  private moveSlashSelection(delta: number): boolean {
    if (this.slashMenu.hidden || this.slashCommands.length === 0) return false;
    this.slashSelection = (
      this.slashSelection + delta + this.slashCommands.length
    ) % this.slashCommands.length;
    this.renderSlashMenu();
    this.slashMenu.querySelector<HTMLElement>(`[data-command-index="${this.slashSelection}"]`)
      ?.scrollIntoView({ block: "nearest" });
    return true;
  }

  private applySelectedSlashCommand(): boolean {
    if (this.slashMenu.hidden) return false;
    const command = this.slashCommands[this.slashSelection];
    if (!command) return false;
    const selection = this.view.state.selection.main;
    const line = this.view.state.doc.lineAt(selection.head);
    const context = this.slashCommandsAt(selection.head)?.context;
    if (!context) return false;

    const from = line.from + context.slashIndex;
    if (command.id === "ai") {
      this.view.dispatch({ changes: { from, to: selection.head, insert: "" } });
      this.hideSlashMenu();
      this.onAskAi?.("cursor");
      return true;
    }
    if (command.id === "table") {
      this.hideSlashMenu();
      this.insertTable(from, selection.head);
      return true;
    }
    const insert = command.id === "today" ? this.getTodayText() : command.insert;
    const selectionRange = command.select ?? [insert.length, insert.length];
    this.view.dispatch({
      changes: { from, to: selection.head, insert },
      selection: EditorSelection.range(from + selectionRange[0], from + selectionRange[1]),
      scrollIntoView: true,
    });
    this.hideSlashMenu();
    this.view.focus();
    return true;
  }

  private dismissSlashMenu(): boolean {
    if (this.slashMenu.hidden) return false;
    this.hideSlashMenu();
    return true;
  }

  private hideSlashMenu(): void {
    this.slashMenu.hidden = true;
    this.slashCommands = [];
    this.slashQuery = "";
  }
}
